import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaWineGlassAlt, FaSmoking, FaCheck } from "react-icons/fa";
import {
  FiKey,
  FiUser,
  FiBell,
  FiFileText,
  FiHeart,
  FiSliders,
  FiLock,
  FiClock,
  FiLogOut,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
  FiChevronLeft,
  FiChevronRight,
  FiEdit,
  FiMail,
  FiPhone,
  FiEye,
  FiEyeOff,
  FiCalendar, 
  FiAlertTriangle, 
  FiMapPin,
  FiX,
  FiCheck,
  FiShield,
  FiDatabase,
  FiInfo,
  FiUserX,
  FiXCircle,
  FiSend,
  FiRefreshCw
} from "react-icons/fi";
import { commonPasswords } from "../../services/passwordUtils";
import "./usercss/UserProfile.css";
import "../registration/registrationcss/Register.css";
import "../staffpages/staffcss/sharedprofile.css";
import { useLocation } from "react-router-dom";
import Footer from "../../components/AppFooter";
import { db } from "../../firebase";
import { auth } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
  serverTimestamp,
  deleteField,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from "firebase/auth";
import { userService, psgcService, notificationService } from "../../services/firestoreService";
import { appointmentService } from "../../services/appointmentService";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";
import UserChangePassword from "../../components/usercomponents/UserChangePassword";
import TwoFactorOtpModal from "../../components/otpmodals/TwoFactorOtpModal";
import UserChangeOtpModal from "../../components/otpmodals/UserChangeOtpModal";
import AccountPanel from "./ProfileAccounts";
import ProfileNotifications from "./ProfileNotifications";
import ProfileContent from "./ProfileContent";
import ProfileAccessibility from "./ProfileAccessibility";
import auravitLogoCircle from "../../images/auravit-logocircle.png";
import alcoholIcon from "../../images/alcohol-icon 1.png";
import cigaretteIcon from "../../images/cigarrete-icon 1.png";
import { sessionService } from "../../services/sessionService";

// Safe localStorage user parse
function getStoredUser() {
  try {
    const s = localStorage.getItem("user");
    return s ? JSON.parse(s) : null;
  } catch (err) {
    console.warn("Invalid stored user", err);
    return null;
  }
}

const menuItems = [
  { id: "account", label: "Account", icon: <FiUser /> },
  { id: "notifications", label: "Notifications", icon: <FiBell /> },
  { id: "content", label: "Content", icon: <FiFileText /> },
  { id: "accessibility", label: "Accessibility", icon: <FiSliders /> },
  { id: "privacy", label: "Data & Privacy", icon: <FiLock /> },
  { id: "logout", label: "Logout", icon: <FiLogOut /> },
  { id: "deactivate", label: "Deactivate Account", icon: <FiAlertTriangle /> },
];

const normalizePhoneDigits = (value) => String(value || "").replace(/\D/g, "");
const getLast10Digits = (value) => normalizePhoneDigits(value).slice(-10);

const RELATIONSHIP_OPTIONS = [
  "Parent",
  "Partner",
  "Guardian",
  "Sibling",
  "Relative",
];

// Firestore optimization: Global shared cache reference for user data across all sub-panels
const sharedUserDataCacheRef = { current: null };

// Firestore optimization: Shared hook for access history to prevent duplicate listeners
const useAccessHistory = (user) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Firestore optimization: Client-side guard to prevent duplicate expiration writes
  const expirationTriggeredRef = useRef(new Set());

  useEffect(() => {
    if (!user?.uid) return;

    // Read from the access_history subcollection
    const q = query(
      collection(db, 'users', user.uid, 'access_history'),
      orderBy('updatedAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const accessRecords = snapshot.docs.map(doc => {
        const data = doc.data();

        // Check for automatic expiration detection (if not already marked as expired in DB)
        if (data.status === 'ACTIVE' && data.expiresAt) {
          const expiryDate = new Date(data.expiresAt);
          const now = new Date();

          // Firestore optimization: Client-side guard to prevent duplicate writes
          if (expiryDate <= now && !data.expirationNotified && !expirationTriggeredRef.current.has(data.appointmentId)) {
            // Trigger the backend update to mark as expired and create notification
            appointmentService.handleDataAccessExpiration(data.appointmentId);
            expirationTriggeredRef.current.add(data.appointmentId); // Mark as triggered
          }
        }

        return {
          id: doc.id,
          ...data
        };
      });
      setHistory(accessRecords);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching access history:", err);
      setLoading(false);
    });

    return () => {
      expirationTriggeredRef.current.clear(); // Clean up on unmount
      unsubscribe();
    };
  }, [user?.uid]); // Removed currentTime from dependencies

  // Helper to get effective status (checks expiration) - computed at render time
  const getEffectiveStatus = (item) => {
    if (item.status === 'ACTIVE' && item.expiresAt) {
      const expiryDate = new Date(item.expiresAt);
      const now = new Date();
      if (expiryDate <= now) {
        return 'EXPIRED';
      }
    }
    return item.status;
  };

  // Helper functions for useAccessHistory (defined inside hook to avoid syntax errors)
  const getStatusBadge = (status) => {
    switch(status) {
      case 'ACTIVE': return { color: '#10b981', text: 'Active' };
      case 'EXPIRED': return { color: '#64748b', text: 'Expired' };
      case 'REVOKED': return { color: '#dc2626', text: 'Revoked by User' };
      case 'DENIED': return { color: '#ef444', text: 'Denied' };
      default: return { color: '#64748b', text: status || 'Unknown' };
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    return hours === 1 ? "1 hour" : `${hours} hours`;
  };

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return 'N/A';
    const date = typeof expiresAt.toDate === 'function'
      ? expiresAt.toDate()
      : new Date(expiresAt);
    return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  const formatAccessPeriod = (approvedAt, expiresAt) => {
    if (!approvedAt || !expiresAt) return 'N/A';
    const start = approvedAt.toDate ? approvedAt.toDate() : new Date(approvedAt);
    const end = new Date(expiresAt);
    return `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
  };

  return {
    history,
    loading,
    currentTime: new Date(), // Computed at return time for components that need it
    getEffectiveStatus,
    getStatusBadge,
    formatDuration,
    formatExpiry,
    formatAccessPeriod
  };
};

// Shared refs for batching across components
const sharedBatchingRefs = {
  pendingUpdates: {},
  saveTimeout: null,
  isSaving: false
};

// Module-level helper function to build update structure
const buildUserUpdateModule = (currentUser, updates) => {
  if (!userService.isGroupedStructure(currentUser)) {
    return { ...updates };
  }

  // Build grouped structure updates using dot notation
  const groupedUpdate = {};
  
  // Personal information updates (excluding firstName/lastName from Firestore)
  // firstName and lastName are not stored in Firestore but kept for UIC generation
  if (updates.sex !== undefined) {
    groupedUpdate.sex = updates.sex;
    groupedUpdate["personalInformation.sex"] = updates.sex;
  }
  if (updates.dob !== undefined) {
    groupedUpdate.dob = updates.dob;
    groupedUpdate["personalInformation.dob"] = updates.dob;
  }
  
  // Contact details updates (keep both root and nested for compatibility)
  if (updates.region !== undefined) {
    groupedUpdate.region = updates.region;
    groupedUpdate["contactDetails.region"] = updates.region;
  }
  if (updates.province !== undefined) {
    groupedUpdate.province = updates.province;
    groupedUpdate["contactDetails.province"] = updates.province;
  }
  if (updates.city !== undefined) {
    groupedUpdate.city = updates.city;
    groupedUpdate["contactDetails.city"] = updates.city;
  }
  if (updates.barangay !== undefined) {
    groupedUpdate.barangay = updates.barangay;
    groupedUpdate["contactDetails.barangay"] = updates.barangay;
  }
  if (updates.contact !== undefined) {
    groupedUpdate.contact = updates.contact;
    groupedUpdate["contactDetails.contact"] = updates.contact;
  }
  if (updates.email !== undefined) {
    groupedUpdate.email = updates.email;
    groupedUpdate["contactDetails.email"] = updates.email;
  }
  
  // Auth and identification fields at root level
  if (updates.emailLower !== undefined) groupedUpdate.emailLower = updates.emailLower;
  if (updates.username !== undefined) groupedUpdate.username = updates.username;
  if (updates.usernameLower !== undefined) groupedUpdate.usernameLower = updates.usernameLower;
  if (updates.phone !== undefined) groupedUpdate.phone = updates.phone;
  if (updates.phoneLast10 !== undefined) groupedUpdate.phoneLast10 = updates.phoneLast10;
  if (updates.password !== undefined) {
    groupedUpdate.password = updates.password;
    groupedUpdate["accountSetup.password"] = updates.password;
  }
  
  return groupedUpdate;
};

// Module-level batched save operation with debouncing
const batchUpdateUser = async (updates, loggedInUser) => {
  if (sharedBatchingRefs.isSaving) {
    // Queue the update if another save is in progress
    Object.assign(sharedBatchingRefs.pendingUpdates, updates);
    return;
  }

  // Merge with any pending updates
  Object.assign(sharedBatchingRefs.pendingUpdates, updates);

  // Clear any existing timeout
  if (sharedBatchingRefs.saveTimeout) {
    clearTimeout(sharedBatchingRefs.saveTimeout);
  }

  // Debounce for 500ms to allow rapid updates to be batched
  sharedBatchingRefs.saveTimeout = setTimeout(async () => {
    sharedBatchingRefs.isSaving = true;
    
    try {
      const finalUpdates = { ...sharedBatchingRefs.pendingUpdates };
      sharedBatchingRefs.pendingUpdates = {};

      if (Object.keys(finalUpdates).length === 0) {
        return;
      }

      const structuredUpdate = buildUserUpdateModule(loggedInUser, finalUpdates);

      await updateDoc(doc(db, "users", loggedInUser.uid), {
        ...structuredUpdate,
        updatedAt: serverTimestamp(),
      });

      // Update local storage and state
      const mergedUser = { ...(loggedInUser || {}) };
      Object.keys(structuredUpdate).forEach(key => {
        if (key.includes('.')) {
          const parts = key.split('.');
          let current = mergedUser;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) current[parts[i]] = {};
            current = current[parts[i]];
          }
          current[parts[parts.length - 1]] = structuredUpdate[key];
        } else {
          mergedUser[key] = structuredUpdate[key];
        }
      });

      localStorage.setItem("user", JSON.stringify(mergedUser));
      
      // Update cache
      sharedUserDataCacheRef.current = mergedUser;
      
      return mergedUser;
    } catch (error) {
      console.error('Batch update failed:', error);
      throw error;
    } finally {
      sharedBatchingRefs.isSaving = false;
      
      // Process any pending updates that arrived during the save
      if (Object.keys(sharedBatchingRefs.pendingUpdates).length > 0) {
        await batchUpdateUser({}, loggedInUser);
      }
    }
  }, 500);
};

// Module-level immediate save (for critical updates that shouldn't be debounced)
const immediateUpdateUser = async (updates, loggedInUser) => {
  // Clear any pending batched updates
  if (sharedBatchingRefs.saveTimeout) {
    clearTimeout(sharedBatchingRefs.saveTimeout);
    sharedBatchingRefs.saveTimeout = null;
  }

  // Merge with any pending updates
  Object.assign(sharedBatchingRefs.pendingUpdates, updates);
  const finalUpdates = { ...sharedBatchingRefs.pendingUpdates };
  sharedBatchingRefs.pendingUpdates = {};

  if (Object.keys(finalUpdates).length === 0) {
    return null;
  }

  const structuredUpdate = buildUserUpdateModule(loggedInUser, finalUpdates);

  await updateDoc(doc(db, "users", loggedInUser.uid), {
    ...structuredUpdate,
    updatedAt: serverTimestamp(),
  });

  // Update local storage and state
  const mergedUser = { ...(loggedInUser || {}) };
  Object.keys(structuredUpdate).forEach(key => {
    if (key.includes('.')) {
      const parts = key.split('.');
      let current = mergedUser;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = structuredUpdate[key];
    } else {
      mergedUser[key] = structuredUpdate[key];
    }
  });

  localStorage.setItem("user", JSON.stringify(mergedUser));
  
  // Update cache
  sharedUserDataCacheRef.current = mergedUser;
  
  return mergedUser;
};

// Cache warming function to ensure shared cache is populated
const warmUserCache = async (user) => {
  if (!user?.uid) return null;
  
  // Check if cache is already populated
  if (sharedUserDataCacheRef.current && sharedUserDataCacheRef.current.uid === user.uid) {
    return sharedUserDataCacheRef.current;
  }
  
  try {
    const userData = await userService.getUser(user.uid);
    sharedUserDataCacheRef.current = userData;
    
    return userData;
  } catch (error) {
    console.error('Failed to warm user cache:', error);
    return null;
  }
};

// ---------------- HealthHistoryPanel ----------------

function HealthHistoryPanel({ showBanner }) {
  const { language } = useLanguage();
  const t = translations[language];
  const CONDITIONS = [
    "Obesity",
    "Liver Disease",
    "Diabetes",
    "Hypertension",
    "Chronic Kidney Disease",
    "Heart Disease",
    "Others (Please Specify)",
  ];

  const ALLERGIES = [
    "Penicillin / Antibiotics",
    "Hormonal Medications (Pills, Patches, Injections)",
    "Others (Please Specify)",
  ];

  const MEDICATION_HISTORY = [
    "IUD", "Implant", "Injection", "Pills",
    "Others (Please Specify)",
  ];

  const INFECTIONS_OPTIONS = [
    "Chlamydia",
    "Gonorrhea",
    "Syphilis",
    "HPV",
    "Hepatitis B",
    "Hepatitis C",
    "Genital Herpes",
    "Trichomoniasis",
    "Others (Please Specify)",
  ];

  const HIV_OPTIONS = [
    "Positive",
    "Negative",
    "Unknown",
  ];

  const CONSULTATION_REASONS = [
    "Family Planning / Contraceptive Guidance",
    "HIV Screening",
  ];

  // -------------------- STATE --------------------
  // Health & Lifestyle
  const [healthEditing, setHealthEditing] = useState(false);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [medicalOther, setMedicalOther] = useState("");
  const [allergies, setAllergies] = useState([]);
  const [allergyOther, setAllergyOther] = useState("");
  const [alcoholUse, setAlcoholUse] = useState("");
  const [smoking, setSmoking] = useState("");
  const [hormonalMedicationText, setHormonalMedicationText] = useState("");
  
  // Original values for Health & Lifestyle (for cancel)
  const [origMedicalHistory, setOrigMedicalHistory] = useState([]);
  const [origMedicalOther, setOrigMedicalOther] = useState("");
  const [origAllergies, setOrigAllergies] = useState([]);
  const [origAllergyOther, setOrigAllergyOther] = useState("");
  const [origAlcoholUse, setOrigAlcoholUse] = useState("");
  const [origSmoking, setOrigSmoking] = useState("");
  const [origHormonalMedicationText, setOrigHormonalMedicationText] = useState("");

  // HIV Status
  const [hivEditing, setHivEditing] = useState(false);
  const [hivSelection, setHivSelection] = useState([]); // checkbox options
  const [stiOther, setStiOther] = useState(""); // Fixed: separate state for STI other
  const [hivLeftBox, setHivLeftBox] = useState(""); // custom "radio" buttons
  const [hivRightBoxText, setHivRightBoxText] = useState("");
  const [lastTestedDate, setLastTestedDate] = useState(""); // Added date state
  
  // Original values for HIV Status (for cancel)
  const [origHivSelection, setOrigHivSelection] = useState([]);
  const [origStiOther, setOrigStiOther] = useState("");
  const [origHivLeftBox, setOrigHivLeftBox] = useState("");
  const [origHivRightBoxText, setOrigHivRightBoxText] = useState("");
  const [origLastTestedDate, setOrigLastTestedDate] = useState("");

  // Additional Health Info
  const [additionalEditing, setAdditionalEditing] = useState(false);
  const [additionalHistory, setAdditionalHistory] = useState([]);
  const [additionalOther, setAdditionalOther] = useState("");
  const [contraceptionOtherText, setContraceptionOtherText] = useState("");
  const [leftBox, setLeftBox] = useState([]);
  const [leftBoxOther, setLeftBoxOther] = useState("");
  const [rightBoxText, setRightBoxText] = useState("");
  
  // Original values for Additional Health Info (for cancel)
  const [origAdditionalHistory, setOrigAdditionalHistory] = useState([]);
  const [origAdditionalOther, setOrigAdditionalOther] = useState("");
  const [origContraceptionOtherText, setOrigContraceptionOtherText] = useState("");
  const [origLeftBox, setOrigLeftBox] = useState([]);
  const [origLeftBoxOther, setOrigLeftBoxOther] = useState("");
  const [origRightBoxText, setOrigRightBoxText] = useState("");

  // Validation states for "Other" fields
  const [healthFieldErrors, setHealthFieldErrors] = useState({});
  const [stiFieldErrors, setStiFieldErrors] = useState({});
  const [additionalFieldErrors, setAdditionalFieldErrors] = useState({});

  // Clinical Services
  const [clinicalEditing, setClinicalEditing] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [emergencyName, setEmergencyName] = useState("");
  const [emergencyRelationship, setEmergencyRelationship] = useState("");
  const [emergencyRelationshipOther, setEmergencyRelationshipOther] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [userSex, setUserSex] = useState("");
  const [isAssistedAccount, setIsAssistedAccount] = useState(false);

  const PAST_SURGERIES = [
    "Abdominal Section",
    "Pelvic Surgery",
    ...(userSex === "male" ? ["Prostate Surgery", "Testicular Surgery"] : []),
    ...(userSex === "female" ? ["Cesarean Section", "Hysterectomy"] : []),
    "Others (Please Specify)"
  ];
  
  // Original values for Clinical Services (for cancel)
  const [origSelectedReasons, setOrigSelectedReasons] = useState([]);
  const [origEmergencyName, setOrigEmergencyName] = useState("");
  const [origEmergencyRelationship, setOrigEmergencyRelationship] = useState("");
  const [origEmergencyRelationshipOther, setOrigEmergencyRelationshipOther] = useState("");
  const [origEmergencyContact, setOrigEmergencyContact] = useState("");

  const ensureOtherSelected = (list, otherText) => {
    if (otherText && !list.includes("Others (Please Specify)")) {
      return [...list, "Others (Please Specify)"];
    }
    return list;
  };

  const STI_ALIASES = {
      chlamydia: "Chlamydia",
      chylamydia: "Chlamydia",
      "other": "Others (Please Specify)",
      "other (please specify)": "Others (Please Specify)",
      "others": "Others (Please Specify)",
      "others (please specify)": "Others (Please Specify)",
      "Other": "Others (Please Specify)",
      "Others (please specify)": "Others (Please Specify)"
    };

  const SURGERY_ALIASES = {
    "ceasrean": "Cesarean Section",
    "ceasrean section": "Cesarean Section",
    "cesarean": "Cesarean Section",
    "cesarean section": "Cesarean Section",
    "other": "Others (Please Specify)",
    "other (please specify)": "Others (Please Specify)",
    "others": "Others (Please Specify)",
    "others (please specify)": "Others (Please Specify)",
    "Other": "Others (Please Specify)",
    "Others (please specify)": "Others (Please Specify)"
  };

  const normalizeSelectionList = (list, aliasMap) => {
    const map = Object.keys(aliasMap).reduce((acc, key) => {
      acc[key.toLowerCase()] = aliasMap[key];
      return acc;
    }, {});
    return (Array.isArray(list) ? list : [])
      .map((item) => {
        const trimmed = String(item || "").trim();
        if (!trimmed) return "";
        return map[trimmed.toLowerCase()] || trimmed;
      })
      .filter(Boolean);
  };

  const OTHER_ALIAS = {
    "other": "Others (Please Specify)",
    "other (please specify)": "Others (Please Specify)",
    "other (Please specify)": "Others (Please Specify)",
    "others": "Others (Please Specify)",
    "others (please specify)": "Others (Please Specify)",
    "others (Please specify)": "Others (Please Specify)",
    "Others": "Others (Please Specify)",
    "Others (please specify)": "Others (Please Specify)",
    "Others (Please specify)": "Others (Please Specify)"
  };

  // Load User Data from Firestore and localStorage
  useEffect(() => {
    const loadData = async () => {
      const storedUser = getStoredUser();
      let firestoreUser = storedUser;
      
      // Try to fetch user data from Firestore first
      if (storedUser?.uid) {
        try {
          firestoreUser = await userService.getUser(storedUser.uid);
        } catch (error) {
          console.warn("Could not fetch user data from Firestore, using stored user instead", error);
        }
      }
      
      const user = firestoreUser || storedUser;
      if (!user) return;

      if (user.personalInformation?.sex) {
        setUserSex(user.personalInformation.sex.toLowerCase());
      } else if (user.sex) {
        setUserSex(user.sex.toLowerCase());
      }

      setIsAssistedAccount(user.assistanceRequired === "yes");
      
      // -------------------- Health & Lifestyle --------------------
      setMedicalHistory(normalizeSelectionList(user.medicalHistory, OTHER_ALIAS));
      setMedicalOther(user.medicalOther || "");
      setAllergies(normalizeSelectionList(user.allergies, OTHER_ALIAS));
      setAllergyOther(user.allergyOther || "");
      setAlcoholUse(user.alcoholUse || "");
      setSmoking(user.smoking || "");
      setHormonalMedicationText(user.hormonalMedications || "");
      
      // -------------------- Sexual History / HIV Status --------------------
      setHivSelection(normalizeSelectionList(user.stiHistory, STI_ALIASES));
      setStiOther(user.stiOther || "");
      setHivLeftBox(user.hivStatus || "");
      setHivRightBoxText(user.hivOther || "");
      setLastTestedDate(user.lastTestedDate || "");
      
      // -------------------- Additional Health Info --------------------
      setAdditionalHistory(normalizeSelectionList(user.additionalHealthHistory, OTHER_ALIAS));
      setAdditionalOther(user.additionalOther || "");
      setContraceptionOtherText(user.contraceptionOther || "");
      setLeftBox(normalizeSelectionList(user.pastSurgeries, SURGERY_ALIASES));
      setLeftBoxOther(user.surgeryOther || "");
      setRightBoxText(user.currentMedications || "");
      
      // -------------------- Emergency Contact --------------------
      setSelectedReasons(normalizeSelectionList(user.consultationReasons, {}));
      // Prioritize nested emergencyContact structure for all fields
      setEmergencyName(user.emergencyContact?.name || user.emergencyContactName || "");
      setEmergencyContact(getLast10Digits(user.emergencyContact?.contact || user.emergencyContactNumber || ""));
      
      // Prioritize nested emergencyContact.relationship over flat field
      const existingRelationship = user.emergencyContact?.relationship || user.emergencyContactRelationship;
      const isExistingRelationshipOption = ["Parent","Partner","Guardian","Sibling","Relative","Other"].includes(existingRelationship);
      
      if (existingRelationship) {
        if (isExistingRelationshipOption) {
          setEmergencyRelationship(existingRelationship);
        } else {
          setEmergencyRelationship("Other");
          setEmergencyRelationshipOther(existingRelationship);
        }
      } else if (user.emergencyContactRelationshipOther) {
        setEmergencyRelationshipOther(user.emergencyContactRelationshipOther);
      } else {
        setEmergencyRelationship("");
      }
      
      if (user.emergencyContactRelationshipOther) {
        setEmergencyRelationshipOther(user.emergencyContactRelationshipOther);
      } else if (!isExistingRelationshipOption && existingRelationship) {
        setEmergencyRelationshipOther(existingRelationship);
      }
    };
    loadData();
  }, []);

  // For "Other" option in relationship dropdown
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  useEffect(() => {
    if (!emergencyRelationship) {
      setIsOtherTyping(false);
      return;
    }
    setIsOtherTyping(!RELATIONSHIP_OPTIONS.includes(emergencyRelationship));
  }, [emergencyRelationship]);

  // ---------------- VALIDATION FOR EMERGENCY CONTACT ----------------
  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case "name":
        if (!value.trim()) newErrors.name = "Name is required";
        else delete newErrors.name;
        break;

      case "relationship":
        if (!value.trim()) newErrors.relationship = "Relationship is required";
        else delete newErrors.relationship;
        break;

      case "contact":
        if (!/^\d{10}$/.test(value)) {
          newErrors.contact = "Contact number must be 10 digits";
        } else if (!value.startsWith("9")) {
          newErrors.contact = "Invalid contact number";
        } else {
          delete newErrors.contact;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };


  // -------------------- HELPERS --------------------
  const toggleItem = (item, list, setter, otherSetter) => {
    if (!(healthEditing || additionalEditing || clinicalEditing)) return;
    if (list.includes(item)) {
      setter(list.filter((i) => i !== item));
      if (item === "Others (Please Specify)" && otherSetter) otherSetter("");
      if (item === "Hormonal Medications (Pills, Patches, Injections)" && otherSetter) otherSetter("");
      
      // Clear validation errors when pill is deselected
      if (healthEditing) {
        if (item === "Others (Please Specify)") {
          setHealthFieldErrors(prev => ({ ...prev, medicalOther: null }));
        } else if (item === "Hormonal Medications (Pills, Patches, Injections)") {
          setHealthFieldErrors(prev => ({ ...prev, hormonalMedicationText: null }));
        }
      } else if (hivEditing && item === "Others (Please Specify)") {
        setStiFieldErrors(prev => ({ ...prev, stiOther: null }));
      } else if (additionalEditing && item === "Others (Please Specify)") {
        setAdditionalFieldErrors(prev => ({ ...prev, contraceptionOtherText: null, leftBoxOther: null }));
      }
    } else {
      setter([...list, item]);
    }
  };

  const validateEmergencyContact = () => {
    const newErrors = {};
    if (!emergencyName.trim()) newErrors.name = "Name is required";
    const relationshipValue = isOtherTyping ? emergencyRelationshipOther : emergencyRelationship;
    if (!relationshipValue.trim()) newErrors.relationship = "Relationship is required";
    if (!emergencyContact.trim()) newErrors.contact = "Contact Number is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -------------------- EDIT / TOGGLE HANDLERS --------------------
  // Health & Lifestyle - store original values on edit
  const handleHealthEdit = () => {
    setOrigMedicalHistory([...medicalHistory]);
    setOrigMedicalOther(medicalOther);
    setOrigAllergies([...allergies]);
    setOrigAllergyOther(allergyOther);
    setOrigAlcoholUse(alcoholUse);
    setOrigSmoking(smoking);
    setOrigHormonalMedicationText(hormonalMedicationText);
    setHealthEditing(true);
  };
  
  // Cancel handler - restore original values and exit edit mode
  const handleHealthCancel = () => {
    setMedicalHistory(origMedicalHistory);
    setMedicalOther(origMedicalOther);
    setAllergies(origAllergies);
    setAllergyOther(origAllergyOther);
    setAlcoholUse(origAlcoholUse);
    setSmoking(origSmoking);
    setHormonalMedicationText(origHormonalMedicationText);
    setHealthEditing(false);
  };
  
    
  // HIV Status - store original values on edit
  const handleHivEdit = () => {
    setOrigHivSelection([...hivSelection]);
    setOrigStiOther(stiOther);
    setOrigHivLeftBox(hivLeftBox);
    setOrigHivRightBoxText(hivRightBoxText);
    setOrigLastTestedDate(lastTestedDate);
    setHivEditing(true);
  };
  
  // Cancel handler - restore original values and exit edit mode
  const handleHivCancel = () => {
    setHivSelection(origHivSelection);
    setStiOther(origStiOther);
    setHivLeftBox(origHivLeftBox);
    setHivRightBoxText(origHivRightBoxText);
    setLastTestedDate(origLastTestedDate);
    setHivEditing(false);
  };
  // Additional Health Info - store original values on edit
  const handleAdditionalEdit = () => {
    setOrigAdditionalHistory([...additionalHistory]);
    setOrigAdditionalOther(additionalOther);
    setOrigContraceptionOtherText(contraceptionOtherText);
    setOrigLeftBox([...leftBox]);
    setOrigLeftBoxOther(leftBoxOther);
    setOrigRightBoxText(rightBoxText);
    setAdditionalEditing(true);
  };
  
  // Cancel handler - restore original values and exit edit mode
  const handleAdditionalCancel = () => {
    setAdditionalHistory(origAdditionalHistory);
    setAdditionalOther(origAdditionalOther);
    setContraceptionOtherText(origContraceptionOtherText);
    setLeftBox(origLeftBox);
    setLeftBoxOther(origLeftBoxOther);
    setRightBoxText(origRightBoxText);
    setAdditionalEditing(false);
  };
  
  // Emergency Contact - store original values on edit
  const handleClinicalEdit = () => {
    setOrigEmergencyName(emergencyName);
    setOrigEmergencyRelationship(emergencyRelationship);
    setOrigEmergencyRelationshipOther(emergencyRelationshipOther);
    setOrigEmergencyContact(emergencyContact);
    setClinicalEditing(true);
  };
  
  // Cancel handler - restore original values and exit edit mode
  const handleClinicalCancel = () => {
    setEmergencyName(origEmergencyName);
    setEmergencyRelationship(origEmergencyRelationship);
    setEmergencyRelationshipOther(origEmergencyRelationshipOther);
    setEmergencyContact(origEmergencyContact);
    setClinicalEditing(false);
  };

  // Save handlers
  const handleHealthSave = async () => {
    try {
      const loggedInUser = getStoredUser();
      if (!loggedInUser?.uid) return;

      const update = {
        medicalHistory: ensureOtherSelected(medicalHistory, medicalOther),
        medicalOther: medicalOther,
        allergies: ensureOtherSelected(allergies, allergyOther),
        allergyOther: allergyOther,
        alcoholUse: alcoholUse,
        smoking: smoking,
        hormonalMedications: hormonalMedicationText,
      };

      // Use batched update for health information
      await batchUpdateUser(update, loggedInUser);

      setHealthEditing(false);
      showBanner("Health information updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showBanner("Failed to update health information.", "error");
    }
  };

  const handleHivSave = async () => {
    try {
      const loggedInUser = getStoredUser();
      if (!loggedInUser?.uid) return;

      const update = {
        stiHistory: ensureOtherSelected(hivSelection, stiOther),
        stiOther: stiOther,
        hivStatus: hivLeftBox,
        hivOther: hivRightBoxText,
        lastTestedDate: lastTestedDate,
      };

      // Use batched update for HIV information
      await batchUpdateUser(update, loggedInUser);

      setHivEditing(false);
      showBanner("STI/HIV information updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showBanner("Failed to update STI/HIV information.", "error");
    }
  };

  const handleAdditionalSave = async () => {
    try {
      const loggedInUser = getStoredUser();
      if (!loggedInUser?.uid) return;

      const update = {
        additionalHealthHistory: ensureOtherSelected(additionalHistory, additionalOther),
        additionalOther: additionalOther,
        hormonalMedications: hormonalMedicationText,
        contraceptionOther: contraceptionOtherText,
        pastSurgeries: ensureOtherSelected(leftBox, leftBoxOther),
        surgeryOther: leftBoxOther,
        currentMedications: rightBoxText,
      };

      // Use batched update for medication history
      await batchUpdateUser(update, loggedInUser);

      setAdditionalEditing(false);
      showBanner("Medication history updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showBanner("Failed to update medication history.", "error");
    }
  };

  const handleClinicalSave = async () => {
    try {
      if (!validateEmergencyContact()) return;

      const loggedInUser = getStoredUser();
      if (!loggedInUser?.uid) return;

      const update = {
        emergencyContactName: emergencyName,
        emergencyContactNumber: emergencyContact,
        emergencyContactRelationship: emergencyRelationship,
        emergencyContactRelationshipOther: emergencyRelationshipOther,
        emergencyContact: {
          name: emergencyName,
          relationship: emergencyRelationship === "Other" ? emergencyRelationshipOther : emergencyRelationship,
          contact: emergencyContact,
        },
      };

      // Use immediate save for emergency contact (critical update)
      await immediateUpdateUser(update, loggedInUser);

      setClinicalEditing(false);
      showBanner("Emergency contact updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showBanner("Failed to update emergency contact.", "error");
    }
  };

  
  // -------------------- RENDER --------------------
  const relationshipSelectValue = RELATIONSHIP_OPTIONS.includes(emergencyRelationship)
    ? emergencyRelationship
    : (emergencyRelationship ? "Other" : "");
  return (
    <div className="hh-container">
      <h2 className="hh-panel-header">{t.healthHistory}</h2>
      <p className="hh-panel-subtext">
        {t.infoShareTailorGuidance}
      </p>

      {/* ---------------- HEALTH & LIFESTYLE ---------------- */}
      <div className="hh-card">
        <div className="hh-section-header">
          <h3 className="hh-section-title">Health & Lifestyle</h3>
          <div className="hh-section-controls">
            {!healthEditing && <FiEdit onClick={handleHealthEdit} className="hh-edit-icon" />}
          </div>
        </div>
        <div className="hh-card-body">
            {/* Medical History */}
            <div className="hh-box">
              <span className="hh-floating-label">Health / Medical History</span>
              <div className="hh-chip-group">
                {CONDITIONS.map((item) => (
                  <button
                    key={item}
                    className={`hh-chip ${medicalHistory.includes(item) ? "hh-chip--selected" : ""} ${!healthEditing && !medicalHistory.includes(item) ? "hh-chip--disabled" : ""}`}
                    disabled={!healthEditing && !medicalHistory.includes(item)}
                    onClick={() => toggleItem(item, medicalHistory, setMedicalHistory, setMedicalOther)}
                  >
                    {item}
                  </button>
                ))}
              </div>
{medicalHistory.includes("Others (Please Specify)") && (
                <div className="hh-char-counter-wrapper">
                  <textarea 
                    type="text" 
                    className="hh-other-input" 
                    placeholder="Please specify" 
                    disabled={!healthEditing} 
                    maxLength={150}
                    rows={3}
                    value={medicalOther} 
                    onChange={(e) => {
                      setMedicalOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                      if (healthFieldErrors.medicalOther) {
                        setHealthFieldErrors(prev => ({ ...prev, medicalOther: null }));
                      }
                    }} 
                  />
                  {healthEditing && <span className="hh-char-counter">{medicalOther.length}/150</span>}
                  {healthEditing && healthFieldErrors.medicalOther && !medicalOther.trim() && (
                    <span className="hh-field-error">{healthFieldErrors.medicalOther}</span>
                  )}
                </div>
              )}

            </div>

            {/* Allergies */}
            <div className="hh-box">
              <span className="hh-floating-label">Medication Allergies</span>
              <div className="hh-chip-group">
                {ALLERGIES.map((item) => (
                  <button
                    key={item}
                    className={`hh-chip ${allergies.includes(item) ? "hh-chip--selected" : ""} ${!healthEditing && !allergies.includes(item) ? "hh-chip--disabled" : ""}`}
                    disabled={!healthEditing && !allergies.includes(item)}
                    onClick={() =>
                      toggleItem(
                        item,
                        allergies,
                        setAllergies,
                        item === "Others (Please Specify)"
                          ? setAllergyOther
                          : item === "Hormonal Medications (Pills, Patches, Injections)"
                            ? setHormonalMedicationText
                            : undefined
                      )
                    }
                  >
                    {item}
                  </button>
                ))}
              </div>
{/* Hormonal Medication Text Input */}
              {allergies.includes("Hormonal Medications (Pills, Patches, Injections)") && (
                <div className="hh-char-counter-wrapper">
                  <textarea 
                    type="text" 
                    className="hh-other-input" 
                    placeholder="Please specify hormonal medications" 
                    disabled={!healthEditing} 
                    maxLength={150}
                    rows={3}
                    value={hormonalMedicationText} 
                    onChange={(e) => {
                      setHormonalMedicationText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                      if (healthFieldErrors.hormonalMedicationText) {
                        setHealthFieldErrors(prev => ({ ...prev, hormonalMedicationText: null }));
                      }
                    }} 
                  />
                  {healthEditing && <span className="hh-char-counter">{hormonalMedicationText.length}/150</span>}
                  {healthEditing && healthFieldErrors.hormonalMedicationText && !hormonalMedicationText.trim() && (
                    <span className="hh-field-error">{healthFieldErrors.hormonalMedicationText}</span>
                  )}
                </div>
              )}

{allergies.includes("Others (Please Specify)") && (
                <div className="hh-char-counter-wrapper">
                  <textarea 
                    type="text" 
                    className="hh-other-input" 
                    placeholder="Please specify" 
                    disabled={!healthEditing} 
                    maxLength={150}
                    rows={3}
                    value={allergyOther} 
                    onChange={(e) => {
                      setAllergyOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                      if (healthFieldErrors.allergyOther) {
                        setHealthFieldErrors(prev => ({ ...prev, allergyOther: null }));
                      }
                    }} 
                  />
                  {healthEditing && <span className="hh-char-counter">{allergyOther.length}/150</span>}
                  {healthEditing && healthFieldErrors.allergyOther && !allergyOther.trim() && (
                    <span className="hh-field-error">{healthFieldErrors.allergyOther}</span>
                  )}
                </div>
              )}

            </div>

            {/* Lifestyle */}
            <div className="hh-box">
              <span className="hh-floating-label">Lifestyle Indicators</span>
              <div className="hh-lifestyle-rows">
                <div className="hh-lifestyle-row-inline">
                  <FaWineGlassAlt className="hh-lifestyle-icon" />
                  <span className="hh-lifestyle-label">Alcohol Use</span>
                  <div className="hh-radio-group">
                    {["Regularly", "Occasionally", "Never"].map((opt) => (
                      <label key={opt} className={`hh-radio-pill ${alcoholUse === opt ? 'hh-radio-pill--checked' : ''}`}>
                        <input type="radio" name="alcohol" disabled={!healthEditing} checked={alcoholUse === opt} onChange={() => setAlcoholUse(opt)} />
                        <div className="hh-custom-radio"></div>
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="hh-lifestyle-row-inline">
                  <FaSmoking className="hh-lifestyle-icon" />
                  <span className="hh-lifestyle-label">Smoking</span>
                  <div className="hh-radio-group">
                    {["Regularly", "Occasionally", "Never"].map((opt) => (
                      <label key={opt} className={`hh-radio-pill ${smoking === opt ? 'hh-radio-pill--checked' : ''}`}>
                        <input type="radio" name="smoking" disabled={!healthEditing} checked={smoking === opt} onChange={() => setSmoking(opt)} />
                        <div className="hh-custom-radio"></div>
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {healthEditing && (
              <div className="admin-form-actions right-aligned">
                <button className="admin-cancel-btn" onClick={handleHealthCancel}>Cancel</button>
                <button className="admin-update-btn" onClick={handleHealthSave}>Save Changes</button>
              </div>
            )}
          </div>
      </div>

      {/* ---------------- HIV STATUS ---------------- */}
      <div className="hh-card">
        <div className="hh-section-header">
          <h3 className="hh-section-title">Sexual & STI History</h3>
          <div className="hh-section-controls">
            {!hivEditing && <FiEdit onClick={handleHivEdit} className="hh-edit-icon" />}
          </div>
        </div>
        <div className="hh-card-body">
            {/* Checkbox Options */}
            <div className="hh-box">
              <span className="hh-floating-label">History of Sexually Trasmitted Infections (STIs)</span>
              <div className="hh-chip-group">
                {INFECTIONS_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    className={`hh-chip ${hivSelection.includes(opt) ? "hh-chip--selected" : ""} ${!hivEditing ? "hh-chip--disabled" : ""}`}
                    disabled={!hivEditing}
                    onClick={() => {
                      if (hivSelection.includes(opt)) {
                        setHivSelection(hivSelection.filter(i => i !== opt));
                        if (opt === "Others (Please Specify)") setStiOther("");
                      } else {
                        setHivSelection([...hivSelection, opt]);
                      }
                    }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
{hivSelection.includes("Others (Please Specify)") && (
                <div className="hh-char-counter-wrapper">
                  <textarea 
                    type="text" 
                    className="hh-other-input" 
                    placeholder="Please specify" 
                    disabled={!hivEditing} 
                    maxLength={150}
                    rows={3}
                    value={stiOther} 
                    onChange={(e) => {
                      setStiOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                      if (stiFieldErrors.stiOther) {
                        setStiFieldErrors(prev => ({ ...prev, stiOther: null }));
                      }
                    }} 
                  />
                  {hivEditing && <span className="hh-char-counter">{stiOther.length}/150</span>}
                  {hivEditing && stiFieldErrors.stiOther && !stiOther.trim() && (
                    <span className="hh-field-error">{stiFieldErrors.stiOther}</span>
                  )}
                </div>
              )}

            </div>

            {/* Select Status: custom square "radio" buttons */}
            <div className="hh-two-column-row">
              <div className="hh-box hh-half-width">
                <span className="hh-floating-label">HIV Status</span>
                <div className="radio-list">
                  {HIV_OPTIONS.map((opt) => (
                    <div key={opt} className="hiv-row-container">
                      <label className="radio-box">
                        <input
                          type="radio"
                          name="hivStatus"
                          disabled={!hivEditing}
                          checked={hivLeftBox === opt}
                          onChange={() => {
                            setHivLeftBox(opt);
                            setErrors((prev) => ({ ...prev, hivStatus: "" }));
                            if (opt === "Unknown") {
                              setLastTestedDate("");
                              setErrors((prev) => ({ ...prev, lastTestedDate: "" }));
                            }
                          }}
                        />
                        <span className="checkmark"></span>
                        <span className="option-text">{opt}</span>
                      </label>
                      {opt !== "Unknown" && hivLeftBox === opt && (
                        <div className="date-input-container">
                          <input
                            type="date"
                            className={`styled-date-input ${errors.lastTestedDate ? "input-error" : ""}`}
                            disabled={!hivEditing}
                            value={lastTestedDate || ""}
                            onChange={(e) => {
                              setLastTestedDate(e.target.value.slice(0, 10));
                              setErrors((prev) => ({ ...prev, lastTestedDate: "" }));
                            }}
                          />
                          {errors.lastTestedDate && (
                            <div className="admin-field-error" style={{ fontSize: '11px' }}>
                              {errors.lastTestedDate}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {errors.hivStatus && (
                    <div className="admin-field-error">
                      {errors.hivStatus}
                    </div>
                  )}
                </div>
              </div>
                <div className="hh-box hh-half-width">
                  <span className="hh-floating-label">History of Genital Infections / Other Conditions</span>
                  <div className="hh-char-counter-wrapper">
                    <textarea 
                      className="hh-other-input" 
                      rows={5} 
                      disabled={!hivEditing} 
                      maxLength={150}
                      value={hivRightBoxText} 
                      onChange={e => setHivRightBoxText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''))} 
                    />
                    {hivEditing && <span className="hh-char-counter">{hivRightBoxText.length}/150</span>}
                  </div>
                </div>
            </div>

            {hivEditing && (
              <div className="admin-form-actions right-aligned">
                <button className="admin-cancel-btn" onClick={handleHivCancel}>Cancel</button>
                <button className="admin-update-btn" onClick={handleHivSave}>Save Changes</button>
              </div>
            )}
          </div>
      </div>

      {/* ---------------- ADDITIONAL HEALTH INFO ---------------- */}
      <div className="hh-card">
        <div className="hh-section-header">
          <h3 className="hh-section-title">Medication History</h3>
          <div className="hh-section-controls">
            {!additionalEditing && <FiEdit onClick={handleAdditionalEdit} className="hh-edit-icon" />}
          </div>
        </div>
        <div className="hh-card-body">
            {/* Other Health Conditions */}
            {userSex !== "male" && (
              <div className="hh-box">
                <span className="hh-floating-label">Currently on Hormonal Contraception</span>
                <div className="hh-chip-group">
                  {MEDICATION_HISTORY.map((item) => (
                    <button
                      key={item}
                      className={`hh-chip ${additionalHistory.includes(item) ? "hh-chip--selected" : ""} ${!additionalEditing && !additionalHistory.includes(item) ? "hh-chip--disabled" : ""}`}
                      disabled={!additionalEditing && !additionalHistory.includes(item)}
                      onClick={() =>
                        toggleItem(item, additionalHistory, setAdditionalHistory, setContraceptionOtherText)
                      }
                    >
                      {item}
                    </button>
                  ))}
                </div>
                {additionalHistory.includes("Others (Please Specify)") && (
                  <div className="hh-char-counter-wrapper">
                    <textarea
                      type="text"
                      className="hh-other-input"
                      placeholder="Please specify"
                      disabled={!additionalEditing}
                      maxLength={150}
                      rows={3}
                      value={contraceptionOtherText}
                      onChange={(e) => {
                        setContraceptionOtherText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                        if (additionalFieldErrors.contraceptionOtherText) {
                          setAdditionalFieldErrors(prev => ({ ...prev, contraceptionOtherText: null }));
                        }
                      }}
                    />
                    {additionalEditing && <span className="hh-char-counter">{contraceptionOtherText.length}/150</span>}
                    {additionalEditing && additionalFieldErrors.contraceptionOtherText && !contraceptionOtherText.trim() && (
                      <span className="hh-field-error">{additionalFieldErrors.contraceptionOtherText}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Left Box / Right Box */}
            <div className="hh-two-column-row">
              <div className="hh-box hh-half-width">
                <span className="hh-floating-label">Past Surgeries</span>
                <div className="hh-chip-group">
                  {PAST_SURGERIES.map((item) => (
                    <button
                      key={item}
                      className={`hh-chip ${leftBox.includes(item) ? "hh-chip--selected" : ""} ${!additionalEditing && !leftBox.includes(item) ? "hh-chip--disabled" : ""}`}
                      disabled={!additionalEditing && !leftBox.includes(item)}
                      onClick={() => toggleItem(item, leftBox, setLeftBox, setLeftBoxOther)}
                    >
                      {item}
                    </button>
                  ))}
                </div>
{leftBox.includes("Others (Please Specify)") && (
                  additionalEditing ? (
                    <div className="hh-char-counter-wrapper">
                      <textarea
                        type="text"
                        className="hh-other-input"
                        placeholder="Please specify"
                        disabled={!additionalEditing}
                        maxLength={150}
                        rows={3}
                        value={leftBoxOther}
                        onChange={(e) => {
                          setLeftBoxOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                          if (additionalFieldErrors.leftBoxOther) {
                            setAdditionalFieldErrors(prev => ({ ...prev, leftBoxOther: null }));
                          }
                        }}
                      />
                      {additionalEditing && <span className="hh-char-counter">{leftBoxOther.length}/150</span>}
                      {additionalEditing && additionalFieldErrors.leftBoxOther && !leftBoxOther.trim() && (
                        <span className="hh-field-error">{additionalFieldErrors.leftBoxOther}</span>
                      )}
                    </div>
                  ) : (
                    <div className="hh-other-input">
                      {leftBoxOther || "-"}
                    </div>
                  )
                )}
              </div>
              <div className="hh-box hh-half-width">
                <span className="hh-floating-label">Current Medication</span>
                {additionalEditing ? (
                  <div className="hh-char-counter-wrapper">
                    <textarea
                      className="hh-other-input"
                      rows={5}
                      disabled={!additionalEditing}
                      maxLength={150}
                      value={rightBoxText}
                      onChange={(e) => setRightBoxText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''))}
                    />
                    {additionalEditing && <span className="hh-char-counter">{rightBoxText.length}/150</span>}
                  </div>
                ) : (
                  <div className="hh-other-input">
                    {rightBoxText || "-"}
                  </div>
                )}
              </div>
            </div>

            {additionalEditing && (
              <div className="admin-form-actions right-aligned">
                <button className="admin-cancel-btn" onClick={handleAdditionalCancel}>Cancel</button>
                <button className="admin-update-btn" onClick={handleAdditionalSave}>Save Changes</button>
              </div>
            )}
          </div>
      </div>


      {/* ---------------- CLINICAL SERVICES ---------------- */}
      <div className="hh-card">
        <div className="hh-section-header">
          <h3 className="hh-section-title">Clinical Services</h3>
          <div className="hh-section-controls">
            {!clinicalEditing && (
              <FiEdit onClick={handleClinicalEdit} className="hh-edit-icon" />
            )}
          </div>
        </div>
        <div className="hh-card-body">
            {/* ---------------- EMERGENCY CONTACT ---------------- */}
            <div className="hh-card hh-emergency-contact-card">
                <legend>Emergency Contact</legend>

                <div className="hh-three-column-row">
                  {/* ---------------- Name ---------------- */}
                  <div className="hh-emergency-field">
                    <fieldset className="hh-fieldset-inner">
                      <legend>Name</legend>
                      <input
                        type="text"
                        placeholder="Full Name"
                        disabled={!clinicalEditing}
                        maxLength={50}
                        value={emergencyName}
                        onChange={(e) => {
                          const sanitized = e.target.value.replace(/[^a-zA-Z'\- ]/g, '');
                          setEmergencyName(sanitized.slice(0, 50));
                          validateField("name", sanitized.slice(0, 50));
                        }}
                        className={errors.name ? "hh-input-error" : ""}
                      />
                    </fieldset>

                    {errors.name && (
                      <p className="hh-error-text">{errors.name}</p>
                    )}
                  </div>

                  {/* ---------------- Relationship ---------------- */}
                  <div className="hh-emergency-field">
                    <fieldset className="hh-fieldset-inner">
                      <legend>Relationship</legend>

                      {clinicalEditing ? (
                        <>
                          <select
                            disabled={!clinicalEditing}
                            value={relationshipSelectValue}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (value === "Other") {
                                setIsOtherTyping(true);
                                if (RELATIONSHIP_OPTIONS.includes(emergencyRelationship)) {
                                  setEmergencyRelationship("Other");
                                }
                              } else {
                                setIsOtherTyping(false);
                                setEmergencyRelationship(value);
                                setEmergencyRelationshipOther("");
                                validateField("relationship", value);
                              }
                            }}
                            className={errors.relationship ? "hh-input-error" : ""}
                          >
                            <option value="" disabled hidden>
                              Select Relationship
                            </option>
                            {RELATIONSHIP_OPTIONS.map((rel) => (
                              <option key={rel} value={rel}>
                                {rel}
                              </option>
                            ))}
                            <option value="Other">Other</option>
                          </select>

                          {/* Show input if typing a custom "Other" value */}
                          {isOtherTyping && (
                            <input
                              type="text"
                              placeholder="Specify Relationship"
                              disabled={!clinicalEditing}
                              value={emergencyRelationshipOther}
                              onChange={(e) => {
                                const sanitized = e.target.value.replace(/[^a-zA-Z]/g, '');
                                setEmergencyRelationshipOther(sanitized.slice(0, 20));
                                validateField("relationship", sanitized.slice(0, 20));
                              }}
                              maxLength={20}
                              className={errors.relationship ? "hh-input-error" : ""}
                              onBlur={() => {
                                if (!emergencyRelationshipOther.trim()) {
                                  validateField("relationship", "");
                                }
                              }}
                            />
                          )}
                        </>
                      ) : (
                        <input
                          type="text"
                          value={isOtherTyping ? emergencyRelationshipOther : emergencyRelationship}
                          readOnly
                          placeholder="Enter relationship"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '12px',
                            border: '2px solid #e2e8f0',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc',
                            color: '#475569'
                          }}
                        />
                      )}
                    </fieldset>

                    {/* Validation error */}
                    {errors.relationship && (
                      <p className="hh-error-text">{errors.relationship}</p>
                    )}
                  </div>
                  {/* ---------------- Contact Number ---------------- */}




                  <div className="hh-emergency-field">
                    <fieldset className="hh-fieldset-inner">
                      <legend>Contact Number</legend>

                      <div className="hh-unique-contact-wrapper">
                        <span className="hh-unique-country-code">+63</span>
                        <input
                          type="text"
                          placeholder="912345678"
                          disabled={!clinicalEditing}
                          value={emergencyContact}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            setEmergencyContact(digits);
                            validateField("contact", digits);
                          }}
                          maxLength={10}
                          className={errors.contact ? "hh-unique-input-error" : ""}
                        />
                      </div>
                    </fieldset>

                    {errors.contact && (
                      <p className="hh-unique-error-text">{errors.contact}</p>
                    )}
                  </div>

                </div>
              </div>


            {/* ---------------- SAVE / CANCEL ---------------- */}
            {clinicalEditing && (
              <div className="admin-form-actions right-aligned">
                <button
                  className="admin-cancel-btn"
                  onClick={handleClinicalCancel}
                >
                  Cancel
                </button>
                <button
                  className="admin-update-btn"
                  onClick={handleClinicalSave}
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>
      </div>


    </div>
  );
}

// ---------------- AccessHistoryTab ----------------
function AccessHistoryTab() {
  const { language } = useLanguage();
  const t = translations[language];
  const [currentPage, setCurrentPage] = useState(1);
  const user = getStoredUser();
  const itemsPerPage = 5;

  // Firestore optimization: Use shared hook to prevent duplicate listeners
  const {
    history,
    loading,
    getEffectiveStatus,
    getStatusBadge,
    formatDuration,
    formatExpiry,
    formatAccessPeriod
  } = useAccessHistory(user);

  // Current time computed at render time for display purposes
  const currentTime = new Date();

  // Transform real data to match enhanced UI structure
  const transformedHistory = history.map(item => {
    const effectiveStatus = getEffectiveStatus(item);
    return {
      ...item,
      staffId: item.proStaffId || item.professionalId || item.proId || 'Unknown',
      staffName: item.proName || item.professionalName || 'Healthcare Professional',
      staffEmail: item.proEmail || item.professionalEmail || item.email || '',
      role: item.proRole || 'Healthcare Professional',
      department: item.proDepartment || 'Medical Services',
      data: item.requestedData || item.dataRequested || "User health records",
      purpose: item.purpose || "Patient care coordination",
      appointment: item.appointmentType || item.serviceType || 'N/A',
      duration: item.durationWeeks ? (item.durationWeeks === 1 ? '1 week' : `${item.durationWeeks} weeks`) : formatAccessPeriod(item.approvedAt || item.updatedAt, item.expiresAt),
      requestDate: item.requestedAt?.toDate ? item.requestedAt.toDate().toLocaleString() : (item.requestedAt ? new Date(item.requestedAt).toLocaleString() : 'N/A'),
      expiryDate: formatExpiry(
        item.status === 'DENIED'
          ? item.deniedAt
          : item.status === 'REVOKED' || item.status === 'TERMINATED'
            ? item.terminatedAt
            : item.expiresAt
      ),
      status: effectiveStatus
    };
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = transformedHistory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(transformedHistory.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <>
      {loading ? (
        <p style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>Loading history...</p>
      ) : history.length === 0 ? (
        <p style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>{t.noAccessHistoryFound}</p>
      ) : (
        <>
          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              <span>Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, history.length)} of {history.length} records</span>
            </div>
            <div className="pagination-controls">
              <button 
                className="pagination-btn pagination-btn--prev" 
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                <FiChevronLeft />
                Previous
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    className={`pagination-number ${currentPage === pageNumber ? 'pagination-number--active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}
              </div>
              
              <button 
                className="pagination-btn pagination-btn--next" 
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
                <FiChevronRight />
              </button>
            </div>
          </div>

          {/* History List */}
          <div className="privacy-list" style={{ marginTop: '24px' }}>
            {currentItems.map((item) => (
              <div key={item.id} className="privacy-card privacy-card--history">
                <div className="privacy-card-header">
                  <div className="requester-info">
                    <div className="requester-avatar">
                      {(item.staffName || 'Unknown').split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="requester-details">
                      <h3 className="requester-name">{item.staffName}</h3>
                      <p className="requester-role">{item.role}</p>
                      <p className="requester-department">{item.department}</p>
                    </div>
                  </div>
                  <div className="status-badge">
                    <div 
                      className="status-dot" 
                      style={{ backgroundColor: getStatusBadge(item.status).color }}
                    />
                    <span>{getStatusBadge(item.status).text}</span>
                  </div>
                </div>

                <div className="privacy-card-body">
                  <div className="data-request">
                    <div className="data-icon">
                      <FiShield />
                    </div>
                    <div className="data-details">
                      <h4 className="data-title">Access Record</h4>
                      <p className="data-description">{item.data}</p>
                      <div className="data-access-info">
                        {item.appointment && (
                          <div className="access-item">
                            <span className="access-label">Appointment</span>
                            <span className="access-value">{item.appointment}</span>
                          </div>
                        )}
                        <div className="access-item">
                          <span className="access-label">Purpose</span>
                          <span className="access-value">{item.purpose}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="request-meta">
                    <div className="meta-item">
                      <span className="meta-label">Medical Professional</span>
                      <span className="meta-value">{item.staffName}</span>
                    </div>
                    {item.staffEmail && (
                      <div className="meta-item">
                        <span className="meta-label">Requester Email</span>
                        <span className="meta-value">{item.staffEmail}</span>
                      </div>
                    )}
                    <div className="meta-item">
                      <span className="meta-label">Access Duration</span>
                      <span className="meta-value">{item.duration}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Access Expired / Terminated At</span>
                      <span className="meta-value">{item.expiryDate}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Requested At</span>
                      <span className="meta-value">{item.requestDate}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ---------------- DataPrivacyPanel ----------------
function DataPrivacyPanel({ showBanner, initialTab = 'pending' }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [activeTab, setActiveTab] = useState(initialTab);
  const [requests, setRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const user = getStoredUser();
  const professionalCacheRef = useRef({});

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  
  // Pagination state
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [approvedCurrentPage, setApprovedCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Fetch pending requests from the same data_privacy subcollection used by Mobile
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'users', user.uid, 'data_privacy'),
      where('status', '==', 'PENDING'),
      limit(50)
    );

    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const pendingRequests = await Promise.all(snapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        let professionalData = null;
        const professionalId = data.proId || data.professionalId;
        const existingName = data.proName || data.professionalName || data.staffName || data.proDisplayName || '';

        if (professionalId) {
          professionalData = professionalCacheRef.current[professionalId] || null;
          if (!professionalData) {
            try {
              const proSnap = await getDoc(doc(db, 'users', professionalId));
              professionalData = proSnap.exists() ? proSnap.data() : null;
              professionalCacheRef.current[professionalId] = professionalData;
            } catch (proErr) {
              console.warn("Could not fetch professional profile for access request:", proErr);
            }
          }
        }

        const fetchedName = professionalData
          ? (professionalData.fullName || professionalData.name || `${professionalData.firstName || ''} ${professionalData.lastName || ''}`.trim())
          : '';

        return {
          id: data.appointmentId || docSnap.id,
          requestId: data.requestId || docSnap.id,
          requestDocId: docSnap.id,
          ...data,
          proName: existingName || fetchedName || 'Healthcare Professional',
          proRole: data.proRole || professionalData?.role || 'Healthcare Professional',
          proDepartment: data.proDepartment || professionalData?.department || 'Medical Services',
          proEmail: data.proEmail || data.professionalEmail || professionalData?.email || ''
        };
      }));
      setRequests(pendingRequests);
      setLoading(false);
      retryCount = 0; // Reset retry count on success
    }, (err) => {
      console.error("Error fetching data access requests:", err);
      retryCount++;

      if (retryCount <= maxRetries) {
        setTimeout(() => {
          // Auto-retry will happen when component re-renders or we could trigger it manually
        }, retryDelay * retryCount);
      } else {
        console.error("Max retries reached for data access requests fetch");
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Fetch approved requests from access_history subcollection
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'users', user.uid, 'access_history'),
      where('status', '==', 'ACTIVE'),
      orderBy('approvedAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const approved = await Promise.all(snapshot.docs.map(async docSnap => {
        const data = docSnap.data();
        const professionalId = data.proId || data.professionalId;
        let professionalData = professionalCacheRef.current[professionalId] || null;
        if (!professionalData && professionalId) {
          const professionalSnap = await getDoc(doc(db, 'users', professionalId));
          professionalData = professionalSnap.exists() ? professionalSnap.data() : null;
          professionalCacheRef.current[professionalId] = professionalData;
        }
        return {
          id: docSnap.id,
          ...data,
          proName: data.proName || 'Healthcare Professional',
          proRole: data.proRole || 'Healthcare Professional',
          proDepartment: data.proDepartment || 'Medical Services',
          proEmail: data.proEmail || data.professionalEmail || professionalData?.email || ''
        };
      }));
      setApprovedRequests(approved);
    }, (err) => {
      console.error("Error fetching approved access requests:", err);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  const handleApprove = async (appointmentId, requestId, durationWeeks) => {
    setProcessingId(appointmentId);
    try {
      await appointmentService.approveDataAccess(appointmentId, requestId, durationWeeks);
      showBanner("Access request approved!", "success");
    } catch (err) {
      console.error("Error approving access:", err);
      showBanner("Failed to approve access request.", "error");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeny = async (appointmentId, requestId) => {
    setProcessingId(appointmentId);
    try {
      await appointmentService.denyDataAccess(appointmentId, requestId);
      showBanner("Access request denied.", "success");
    } catch (err) {
      console.error("Error denying access:", err);
      showBanner("Failed to deny access request.", "error");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRevoke = async (request) => {
    // request.appointmentId is the actual appointments doc ID
    const appointmentId = request.appointmentId;
    const requestId = request.requestId || request.id;
    if (!appointmentId) {
      showBanner("Unable to revoke: appointment reference not found.", "error");
      return;
    }
    setProcessingId(requestId);
    try {
      await appointmentService.terminateDataAccess(appointmentId, requestId, true);
      showBanner("Access has been revoked successfully.", "success");
    } catch (err) {
      console.error("Error revoking access:", err);
      showBanner("Failed to revoke access.", "error");
    } finally {
      setProcessingId(null);
    }
  };

  const getDurationLabel = (weeks) => {
    if (!weeks) return "1 week";
    return weeks === 1 ? "1 week" : `${weeks} weeks`;
  };

  const formatRequestDateTime = (value) => {
    if (!value) return 'N/A';
    try {
      const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
      return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
    } catch (e) {
      console.error("Error formatting date:", e);
      return 'N/A';
    }
  };

  const getRequestProfessionalName = (request) => (
    request.proName ||
    request.professionalName ||
    request.staffName ||
    request.proDisplayName ||
    'Healthcare Professional'
  );

  const getRequestProfessionalEmail = (request) => (
    request.proEmail ||
    request.professionalEmail ||
    request.email ||
    ''
  );

  const getUrgencyColor = (urgency) => {
    switch(urgency) {
      case 'high': return '#ef4444';
      case 'normal': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#64748b';
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return { color: '#f59e0b', text: 'Pending Review' };
      case 'active': return { color: '#10b981', text: 'Active Access' };
      case 'expired': return { color: '#64748b', text: 'Expired' };
      default: return { color: '#64748b', text: 'Unknown' };
    }
  };

  // Transform real data to match enhanced UI structure
  const transformedRequests = requests.map(req => {
    // Robust date formatting
    let formattedDate = 'Just now';
    if (req.requestedAt) {
      try {
        if (typeof req.requestedAt.toDate === 'function') {
          formattedDate = req.requestedAt.toDate().toLocaleString();
        } else {
          formattedDate = new Date(req.requestedAt).toLocaleString();
        }
      } catch (e) {
        console.error("Error formatting date:", e);
      }
    }

    return {
      ...req,
      staffId: req.proStaffId || req.professionalId || req.proId || 'Unknown',
      staffName: getRequestProfessionalName(req),
      staffEmail: getRequestProfessionalEmail(req),
      role: req.proRole || 'Healthcare Professional',
      department: req.proDepartment || 'Medical Services',
      data: req.requestedData || req.dataRequested || "Appointment records",
      appointment: req.appointmentType || req.serviceType || 'N/A',
      purpose: req.purpose || "Patient care coordination",
      duration: getDurationLabel(req.durationWeeks || 1),
      requestDate: formattedDate,
      reference: req.reference || req.appointmentReference || 'N/A',
      urgency: 'normal',
      status: 'pending'
    };
  });

  // Transform approved requests to match UI structure
  const transformedApprovedRequests = approvedRequests.map(req => {
    // Robust date formatting for approvedAt
    let formattedDate = 'Just now';
    if (req.approvedAt) {
      try {
        if (typeof req.approvedAt.toDate === 'function') {
          formattedDate = req.approvedAt.toDate().toLocaleString();
        } else {
          formattedDate = new Date(req.approvedAt).toLocaleString();
        }
      } catch (e) {
        console.error("Error formatting date:", e);
      }
    }

    return {
      ...req,
      staffId: req.proStaffId || req.professionalId || req.proId || 'Unknown',
      staffName: getRequestProfessionalName(req),
      staffEmail: getRequestProfessionalEmail(req),
      role: req.proRole || 'Healthcare Professional',
      department: req.proDepartment || 'Medical Services',
      data: req.requestedData || req.dataRequested || "Appointment records",
      appointment: req.appointmentType || req.serviceType || 'N/A',
      purpose: req.purpose || "Patient care coordination",
      duration: getDurationLabel(req.durationWeeks || 1),
      requestDate: formattedDate,
      expiryDate: formatRequestDateTime(req.expiresAt),
      reference: req.reference || req.appointmentReference || 'N/A',
      urgency: 'normal',
      status: 'active'
    };
  });

  // Calculate stats for header
  const pendingCount = requests.length;
  const activeCount = approvedRequests.filter(req => {
    // Check if the request is still active (not expired)
    if (req.expiresAt) {
      const expiryDate = req.expiresAt.toDate ? req.expiresAt.toDate() : new Date(req.expiresAt);
      return expiryDate > new Date();
    }
    return true; // If no expiry date, consider it active
  }).length;
  
  // Pagination for pending requests
  const pendingTotalPages = Math.ceil(requests.length / itemsPerPage);
  const pendingStartIndex = (pendingCurrentPage - 1) * itemsPerPage;
  const pendingEndIndex = pendingStartIndex + itemsPerPage;
  const paginatedPendingRequests = transformedRequests.slice(pendingStartIndex, pendingEndIndex);
  
  // Pagination for approved requests
  const approvedTotalPages = Math.ceil(approvedRequests.length / itemsPerPage);
  const approvedStartIndex = (approvedCurrentPage - 1) * itemsPerPage;
  const approvedEndIndex = approvedStartIndex + itemsPerPage;
  const paginatedApprovedRequests = transformedApprovedRequests.slice(approvedStartIndex, approvedEndIndex);
  
  // Pagination handlers
  const handlePendingPrevPage = () => setPendingCurrentPage(prev => Math.max(1, prev - 1));
  const handlePendingNextPage = () => setPendingCurrentPage(prev => Math.min(pendingTotalPages, prev + 1));
  const handlePendingPageChange = (pageNumber) => setPendingCurrentPage(pageNumber);
  
  const handleApprovedPrevPage = () => setApprovedCurrentPage(prev => Math.max(1, prev - 1));
  const handleApprovedNextPage = () => setApprovedCurrentPage(prev => Math.min(approvedTotalPages, prev + 1));
  const handleApprovedPageChange = (pageNumber) => setApprovedCurrentPage(pageNumber);
  
  // Reset pagination when switching tabs
  useEffect(() => {
    setPendingCurrentPage(1);
    setApprovedCurrentPage(1);
  }, [activeTab]);

  return (
    <div className="privacy-panel">
      {/* Header Section */}
      <div className="privacy-header">
        <div className="privacy-header-text">
          <h2 className="privacy-title">{t.privacy}</h2>
          <p className="privacy-subtitle">
            {t.monitorManageAccess}
          </p>
        </div>
        <div className="privacy-stats">
          <div className="privacy-stat">
            <span className="stat-number">{pendingCount}</span>
            <div className="stat-meta">
              <span className="stat-dot stat-dot--pending"></span>
              <span className="stat-label">{t.pending}</span>
            </div>
          </div>
          <div className="privacy-stat">
            <span className="stat-number">{activeCount}</span>
            <div className="stat-meta">
              <span className="stat-dot stat-dot--active"></span>
              <span className="stat-label">{t.active}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="privacy-tabs">
        <button 
          className={`privacy-tab ${activeTab === 'pending' ? 'privacy-tab--active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          {t.pendingRequests}
        </button>
        <button 
          className={`privacy-tab ${activeTab === 'approved' ? 'privacy-tab--active' : ''}`}
          onClick={() => setActiveTab('approved')}
        >
          {t.approvedAccess}
        </button>
        <button 
          className={`privacy-tab ${activeTab === 'history' ? 'privacy-tab--active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          {t.accesshistory}
        </button>
      </div>

      {/* Content */}
      <div className="privacy-content">
        {activeTab === 'pending' || activeTab === 'approved' ? (
          <>
            {loading ? (
              <p style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>Loading requests...</p>
            ) : activeTab === 'pending' && requests.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>{t.noPendingAccessRequests}</p>
            ) : activeTab === 'approved' && approvedRequests.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>No approved access requests.</p>
            ) : activeTab === 'pending' ? (
              <>
                {pendingTotalPages > 1 && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      <span>Showing {pendingStartIndex + 1}-{Math.min(pendingEndIndex, requests.length)} of {requests.length} records</span>
                    </div>
                    <div className="pagination-controls">
                      <button
                        className="pagination-btn pagination-btn--prev"
                        onClick={handlePendingPrevPage}
                        disabled={pendingCurrentPage === 1}
                      >
                        <FiChevronLeft />
                        Previous
                      </button>

                      <div className="pagination-numbers">
                        {Array.from({ length: pendingTotalPages }, (_, index) => index + 1).map((pageNumber) => (
                          <button
                            key={pageNumber}
                            className={`pagination-number ${pendingCurrentPage === pageNumber ? 'pagination-number--active' : ''}`}
                            onClick={() => handlePendingPageChange(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        ))}
                      </div>

                      <button
                        className="pagination-btn pagination-btn--next"
                        onClick={handlePendingNextPage}
                        disabled={pendingCurrentPage === pendingTotalPages}
                      >
                        Next
                        <FiChevronRight />
                      </button>
                    </div>
                  </div>
                )}
                <div className="privacy-list">
                  {paginatedPendingRequests.map((request) => (
                  <div key={request.id} className="privacy-card privacy-card--pending">
                    <div className="privacy-card-header">
                      <div className="requester-info">
                        <div className="requester-avatar">
                          {(request.staffName || 'Unknown').split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="requester-details">
                          <h3 className="requester-name">{request.staffName}</h3>
                          <p className="requester-role">{request.role}</p>
                          <p className="requester-department">{request.department}</p>
                          {request.staffEmail && <p className="requester-department">{request.staffEmail}</p>}
                        </div>
                      </div>
                      <div className="status-badge">
                        <div
                          className="status-dot"
                          style={{ backgroundColor: getStatusBadge(request.status).color }}
                        />
                        <span>{getStatusBadge(request.status).text}</span>
                      </div>
                    </div>

                    <div className="privacy-card-body">
                      <div className="data-access-info">
                        <div className="access-item">
                          <span className="access-label">Data Requested</span>
                          <span className="access-value">{request.data}</span>
                        </div>
                        <div className="access-item">
                          <span className="access-label">Appointment Type</span>
                          <span className="access-value">{request.appointment}</span>
                        </div>
                        <div className="access-item">
                          <span className="access-label">Purpose</span>
                          <span className="access-value">{request.purpose}</span>
                        </div>
                      </div>
                      <div className="request-meta">
                        <div className="meta-item">
                          <span className="meta-label">Medical Professional</span>
                          <span className="meta-value">{request.staffName}</span>
                        </div>
                        {request.staffEmail && (
                          <div className="meta-item">
                            <span className="meta-label">Requester Email</span>
                            <span className="meta-value">{request.staffEmail}</span>
                          </div>
                        )}
                        <div className="meta-item">
                          <span className="meta-label">Access Duration</span>
                          <span className="meta-value">{request.duration}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Requested At</span>
                          <span className="meta-value">{request.requestDate}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Status</span>
                          <span className="meta-value">Pending</span>
                        </div>
                      </div>
                    </div>

                    <div className="privacy-card-footer">
                      <div className="privacy-actions">
                        <button className="privacy-btn privacy-btn--deny" onClick={() => handleDeny(request.appointmentId, request.requestId || request.id)} disabled={processingId === request.requestId || processingId === request.id}>
                          <FiX />
                          {processingId === request.requestId || processingId === request.id ? "Processing..." : "Deny"}
                        </button>
                        <button className="privacy-btn privacy-btn--approve" onClick={() => handleApprove(request.appointmentId, request.requestId || request.id, request.durationWeeks)} disabled={processingId === request.requestId || processingId === request.id}>
                          <FiCheck />
                          {processingId === request.requestId || processingId === request.id ? "Processing..." : "Approve"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              </>
            ) : (
              <>
                {approvedTotalPages > 1 && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      <span>Showing {approvedStartIndex + 1}-{Math.min(approvedEndIndex, approvedRequests.length)} of {approvedRequests.length} records</span>
                    </div>
                    <div className="pagination-controls">
                      <button
                        className="pagination-btn pagination-btn--prev"
                        onClick={handleApprovedPrevPage}
                        disabled={approvedCurrentPage === 1}
                      >
                        <FiChevronLeft />
                        Previous
                      </button>

                      <div className="pagination-numbers">
                        {Array.from({ length: approvedTotalPages }, (_, index) => index + 1).map((pageNumber) => (
                          <button
                            key={pageNumber}
                            className={`pagination-number ${approvedCurrentPage === pageNumber ? 'pagination-number--active' : ''}`}
                            onClick={() => handleApprovedPageChange(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        ))}
                      </div>

                      <button
                        className="pagination-btn pagination-btn--next"
                        onClick={handleApprovedNextPage}
                        disabled={approvedCurrentPage === approvedTotalPages}
                      >
                        Next
                        <FiChevronRight />
                      </button>
                    </div>
                  </div>
                )}
                <div className="privacy-list">
                  {paginatedApprovedRequests.map((request) => (
                  <div key={request.id} className="privacy-card privacy-card--approved">
                    <div className="privacy-card-header">
                      <div className="requester-info">
                        <div className="requester-avatar">
                          {(request.staffName || 'Unknown').split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="requester-details">
                          <h3 className="requester-name">{request.staffName}</h3>
                          <p className="requester-role">{request.role}</p>
                          <p className="requester-department">{request.department}</p>
                          {request.staffEmail && <p className="requester-department">{request.staffEmail}</p>}
                        </div>
                      </div>
                      <div className="status-badge">
                        <div 
                          className="status-dot" 
                          style={{ backgroundColor: getStatusBadge(request.status).color }}
                        />
                        <span>{getStatusBadge(request.status).text}</span>
                      </div>
                    </div>

                    <div className="privacy-card-body">
                      <div className="data-access-info">
                        <div className="access-item">
                          <span className="access-label">Data Requested</span>
                          <span className="access-value">{request.data}</span>
                        </div>
                        <div className="access-item">
                          <span className="access-label">Appointment Type</span>
                          <span className="access-value">{request.appointment}</span>
                        </div>
                        <div className="access-item">
                          <span className="access-label">Purpose</span>
                          <span className="access-value">{request.purpose}</span>
                        </div>
                      </div>
                      <div className="request-meta">
                        <div className="meta-item">
                          <span className="meta-label">Medical Professional</span>
                          <span className="meta-value">{request.staffName}</span>
                        </div>
                        {request.staffEmail && (
                          <div className="meta-item">
                            <span className="meta-label">Requester Email</span>
                            <span className="meta-value">{request.staffEmail}</span>
                          </div>
                        )}
                        <div className="meta-item">
                          <span className="meta-label">Access Duration</span>
                          <span className="meta-value">{request.duration}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Approved At</span>
                          <span className="meta-value">{request.requestDate}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Access Expires</span>
                          <span className="meta-value">{request.expiryDate}</span>
                        </div>
                      </div>
                    </div>

                    <div className="privacy-card-footer">
                      <div className="privacy-actions">
                        <button className="privacy-btn privacy-btn--deny" onClick={() => handleRevoke(request)} disabled={processingId === request.id}>
                          <FiX />
                          {processingId === request.id ? "Processing..." : "Revoke"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              </>
            )}
          </>
        ) : (
          <AccessHistoryTab />
        )}
      </div>
    </div>
  );
}


// ---------------- AccessHistoryPanel ----------------
function AccessHistoryPanel() {
  const { language } = useLanguage();
  const t = translations[language];
  const [currentPage, setCurrentPage] = useState(1);
  const [hasLoaded, setHasLoaded] = useState(false);
  const user = getStoredUser();
  const itemsPerPage = 5;

  // Firestore optimization: Use shared hook to prevent duplicate listeners
  const {
    history,
    loading,
    getEffectiveStatus,
    getStatusBadge,
    formatDuration,
    formatExpiry,
    formatAccessPeriod
  } = useAccessHistory(user);

  // Track if data has been loaded at least once
  useEffect(() => {
    if (!loading && history.length > 0) {
      setHasLoaded(true);
    }
  }, [loading, history]);

  // Transform real data to match enhanced UI structure
  const transformedHistory = history.map(item => {
    const effectiveStatus = getEffectiveStatus(item);
    return {
      ...item,
      staffId: item.proStaffId || item.professionalId || item.proId || 'Unknown',
      staffName: item.proName || item.professionalName || 'Healthcare Professional',
      role: item.proRole || 'Healthcare Professional',
      department: item.proDepartment || 'Medical Services',
      data: item.requestedData || item.dataRequested || "User health records",
      purpose: item.purpose || "Patient care coordination",
      appointment: item.appointmentType || item.serviceType || 'N/A',
      duration: item.durationWeeks ? (item.durationWeeks === 1 ? '1 week' : `${item.durationWeeks} weeks`) : formatAccessPeriod(item.approvedAt || item.updatedAt, item.expiresAt),
      requestDate: item.requestedAt?.toDate ? item.requestedAt.toDate().toLocaleString() : (item.requestedAt ? new Date(item.requestedAt).toLocaleString() : 'N/A'),
      expiryDate: formatExpiry(
        item.status === 'DENIED'
          ? item.deniedAt
          : item.status === 'REVOKED' || item.status === 'TERMINATED'
            ? item.terminatedAt
            : item.expiresAt
      ),
      status: effectiveStatus
    };
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = transformedHistory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(transformedHistory.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="privacy-panel">
      {/* Header Section */}
      <div className="privacy-header">
        <div className="privacy-header-text">
          <h2 className="privacy-title">{t.accesshistory}</h2>
          <p className="privacy-subtitle">
            {t.reviewPastCurrentAccess}
          </p>
        </div>
        <div className="privacy-stats">
          <div className="privacy-stat">
            <span className="stat-number">{transformedHistory.filter(item => item.status === 'ACTIVE').length}</span>
            <span className="stat-label">{t.active}</span>
          </div>
          <div className="privacy-stat">
            <span className="stat-number">{transformedHistory.filter(item => item.status === 'EXPIRED').length}</span>
            <span className="stat-label">{t.expired}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="privacy-content">
        {loading && !hasLoaded ? (
          // Skeleton loading for privacy cards
          [...Array(3)].map((_, i) => (
            <div key={i} className="privacy-card skeleton-privacy-card">
              <div className="privacy-card-header">
                <div className="requester-info">
                  <div className="skeleton skeleton-avatar"></div>
                  <div className="requester-details">
                    <div className="skeleton skeleton-name"></div>
                    <div className="skeleton skeleton-role"></div>
                    <div className="skeleton skeleton-department"></div>
                  </div>
                </div>
                <div className="skeleton skeleton-badge"></div>
              </div>
              <div className="privacy-card-body">
                <div className="access-details">
                  <div className="skeleton skeleton-access-item"></div>
                  <div className="skeleton skeleton-access-item"></div>
                </div>
                <div className="request-meta">
                  <div className="skeleton skeleton-meta-item"></div>
                  <div className="skeleton skeleton-meta-item"></div>
                </div>
              </div>
            </div>
          ))
        ) : history.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>{t.noAccessHistoryFound}</p>
        ) : (
          <>
            {/* Pagination */}
            <div className="pagination-container">
              <div className="pagination-info">
                <span>Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, history.length)} of {history.length} records</span>
              </div>
              <div className="pagination-controls">
                <button 
                  className="pagination-btn pagination-btn--prev" 
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  <FiChevronLeft />
                  Previous
                </button>
                
                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                    <button
                      key={pageNumber}
                      className={`pagination-number ${currentPage === pageNumber ? 'pagination-number--active' : ''}`}
                      onClick={() => handlePageChange(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>
                
                <button 
                  className="pagination-btn pagination-btn--next" 
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <FiChevronRight />
                </button>
              </div>
            </div>

            <div className="privacy-list">
              {currentItems.map((item) => (
                <div key={item.id} className={`privacy-card ${item.status === 'ACTIVE' ? 'privacy-card--approved' : 'privacy-card--expired'}`}>
                  <div className="privacy-card-header">
                    <div className="requester-info">
                      <div className="requester-avatar">
                        {(item.staffName || 'Unknown').split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="requester-details">
                        <h3 className="requester-name">{item.staffName}</h3>
                        <p className="requester-role">{item.role}</p>
                        <p className="requester-department">{item.department}</p>
                      </div>
                    </div>
                    <div className="access-badge">
                      {item.status === 'ACTIVE' ? <FiShield /> : <FiClock />}
                      <span>{item.status === 'ACTIVE' ? 'Active Access' : item.status === 'REVOKED' ? 'Revoked by User' : 'Expired'}</span>
                    </div>
                  </div>

                  <div className="privacy-card-body">
                    <div className="data-request">
                      <div className="data-icon">
                        <FiDatabase />
                      </div>
                      <div className="data-details">
                        <h4 className="data-title">Data Accessed</h4>
                        <p className="data-description">{item.data}</p>
                        <div className="data-access-info">
                          <div className="access-item">
                            <span className="access-label">Purpose</span>
                            <span className="access-value">{item.purpose}</span>
                          </div>
                          {item.appointment && (
                            <div className="access-item">
                              <span className="access-label">Service</span>
                              <span className="access-value">{item.appointment}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="request-meta">
                      <div className="meta-item">
                        <span className="meta-label">Medical Professional</span>
                        <span className="meta-value">{item.staffName}</span>
                      </div>
                      {item.staffEmail && (
                        <div className="meta-item">
                          <span className="meta-label">Requester Email</span>
                          <span className="meta-value">{item.staffEmail}</span>
                        </div>
                      )}
                      <div className="meta-item">
                        <span className="meta-label">Access Duration</span>
                        <span className="meta-value">{item.duration}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Access Expired / Terminated At</span>
                        <span className="meta-value">{item.expiryDate}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Requested At</span>
                        <span className="meta-value">{item.requestDate}</span>
                      </div>
                    </div>
                  </div>

                  <div className="privacy-card-footer">
                    <div className="status-badge">
                      <div 
                        className="status-dot" 
                        style={{ backgroundColor: getStatusBadge(item.status).color }}
                      />
                      <span>{getStatusBadge(item.status).text}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ---------------- Logout ----------------
function LogoutModal({ onClose, t }) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    sessionStorage.setItem('logoutInProgress', 'true');
    try {
      // Delete the current session document directly — the user is still
      // authenticated here so Firestore rules allow it, and it's simpler
      // and more reliable than going through the Cloud Function.
      const currentUser = auth.currentUser;
      if (currentUser) {
        await sessionService.deleteCurrentSession(currentUser.uid);
      }
      sessionService.clearCurrentSession();

      await auth.signOut();
      localStorage.clear();
      sessionStorage.removeItem('freshLoginInProgress');
      navigate("/login", { replace: true });
    } catch (e) {
      console.log("Sign out error:", e);
      sessionService.clearCurrentSession();
      localStorage.clear();
      sessionStorage.removeItem('freshLoginInProgress');
      navigate("/login", { replace: true });
    } finally {
      setTimeout(() => sessionStorage.removeItem('logoutInProgress'), 1000);
    }
  };

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="logout-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="logout-modal-close" onClick={onClose}>
          <FiX />
        </button>

        {/* Icon with animated background */}
        <div className="logout-modal-icon-wrapper">
          <div className="logout-modal-icon-bg">
            <FiLogOut />
          </div>
        </div>

        {/* Content */}
        <div className="logout-modal-content">
          <h2 className="logout-modal-title">{t.readyToLeave}</h2>
          <p className="logout-modal-subtext">
            {t.sessionEndRedirect}
          </p>
        </div>

        {/* Action buttons */}
        <div className="logout-modal-actions">
          <button className="logout-modal-btn cancel" onClick={onClose}>
            <span className="btn-text">{t.stayLoggedIn}</span>
          </button>
          <button className="logout-modal-btn confirm" onClick={handleLogout}>
            <span className="btn-text">{t.logout}</span>
            <FiLogOut className="btn-icon" />
          </button>
        </div>

        {/* Additional info */}
        <div className="logout-modal-footer">
          <div className="logout-modal-security-info">
            <FiShield className="security-icon" />
            <span>{t.yourDataSafelySaved}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Deactivate ----------------
function DeactivateModal({ onClose, onNext, t }) {
  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="deactivate-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="deactivate-modal-close" onClick={onClose}>
          <FiX />
        </button>

        {/* Scrollable content wrapper */}
        <div className="deactivate-modal-content-wrapper">
          {/* Content */}
          <div className="deactivate-modal-content">
            <h2 className="deactivate-modal-title">{t.thinkingAboutLeaving}</h2>
            <p className="deactivate-modal-subtext">
              {t.understandSometimesNeedBreak}
            </p>
          </div>

          {/* Impact cards */}
          <div className="deactivate-modal-impacts">
            <div className="impact-card">
              <div className="impact-icon">
                <FiClock />
              </div>
              <div className="impact-content">
                <h4>{t.temporaryPause}</h4>
                <p>{t.profileHiddenButDataStored}</p>
              </div>
            </div>
            
            <div className="impact-card">
              <div className="impact-icon">
                <FiShield />
              </div>
              <div className="impact-content">
                <h4>{t.easyReactivation}</h4>
                <p>{t.logBackInWithin30Days}</p>
              </div>
            </div>
            
            <div className="impact-card">
              <div className="impact-icon">
                <FiBell />
              </div>
              <div className="impact-content">
                <h4>{t.noNotifications}</h4>
                <p>{t.noNotificationsWhileDeactivated}</p>
              </div>
            </div>
          </div>

          {/* Policy summary */}
          <div className="deactivate-modal-policy">
            <div className="policy-header">
              <FiDatabase />
              <h4>{t.accountLifecyclePolicy}</h4>
            </div>
            <div className="policy-content">
              <p>{t.toMaintainSystemIntegrity}</p>
              <ul>
                <li>{t.twelveMonthsInactive}</li>
                <li>{t.thirtyDaysAfterNotification}</li>
                <li>{t.threeYearsInactive}</li>
              </ul>
              <p className="policy-note">{t.dataNeverDeleted}</p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="deactivate-modal-actions">
          <button className="deactivate-modal-btn cancel" onClick={onClose}>
            <span className="btn-text">{t.keepMyAccountActive}</span>
          </button>
          <button className="deactivate-modal-btn confirm" onClick={onNext}>
            <span className="btn-text">{t.deactivateAccount}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Deactivate (Password Confirmation) ----------------
function DeactivatePasswordModal({ onClose, onNext, showBanner, t }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState("");
  const [error, setError] = useState("");
  const [deactivationReasonError, setDeactivationReasonError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!password.trim()) {
      setError(t.passwordRequired);
      return;
    }

    if (!deactivationReason.trim()) {
      setDeactivationReasonError(t.deactivationReasonRequired);
      return;
    }

    if (!/^[A-Za-z\s]+$/.test(deactivationReason.trim())) {
      setDeactivationReasonError(t.deactivationReasonLettersOnly);
      return;
    }

    if (deactivationReason.trim().length > 20) {
      setDeactivationReasonError(t.deactivationReason20Chars);
      return;
    }

    setIsLoading(true);
    const storedUser = getStoredUser();

    try {
      const authUser = auth.currentUser;
      if (!authUser) {
        setError(t.sessionExpiredPleaseLogIn);
        return;
      }
      const email = String(storedUser?.email || authUser.email || "");

      // Get user's name
      const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
      const localStorageFirstName = storedDraft.firstName || '';
      const localStorageLastName = storedDraft.lastName || '';
      const userName = [localStorageFirstName, localStorageLastName]
        .filter(Boolean)
        .join(" ")
        .trim() || storedUser?.uniqueIdentifierCode || "User";

      const credential = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(authUser, credential);

      // Store pending deactivation (don't deactivate yet!)
      const pendingDeactivation = {
        uid: authUser.uid,
        email,
        firstName: localStorageFirstName,
        lastName: localStorageLastName,
        userName,
        timestamp: Date.now(),
        type: 'deactivate-account',
        deactivationReason: deactivationReason.trim()
      };
      localStorage.setItem('pendingDeactivation', JSON.stringify(pendingDeactivation));

      // Move to OTP step
      onNext?.();

    } catch (err) {
      console.error('Password verification failed:', err);
      setError(t.incorrectPasswordPleaseTryAgain);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="deactivate-modal-card deactivate-security-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="deactivate-modal-close" onClick={onClose}>
          <FiX />
        </button>

        {/* Scrollable content wrapper */}
        <div className="deactivate-modal-content-wrapper">

          {/* Content */}
          <div className="deactivate-modal-content">
            <h2 className="deactivate-modal-title" style={{ color: '#000000' }}>{t.securityVerification}</h2>
            <p className="deactivate-modal-subtext">
              {t.forYourProtectionEnterPassword}
            </p>
          </div>


          {/* Deactivation reason field */}
          <div className="deactivate-reason-field">
            <div className="reason-input-wrapper">
              <label htmlFor="deactivation-reason" className="reason-label">
                {t.deactivationReason} <span className="required">*</span>
              </label>
              <div className="reason-input-container">
                <input
                  id="deactivation-reason"
                  type="text"
                  placeholder={t.enterDeactivationReason}
                  value={deactivationReason}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow letters and spaces
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      setDeactivationReason(value);
                      setDeactivationReasonError("");
                    }
                  }}
                  className={`reason-input ${deactivationReasonError ? "error" : ""}`}
                  maxLength={20}
                />
              </div>

              {/* Error message */}
              {deactivationReasonError && (
                <div className="reason-error-message">
                  <FiAlertTriangle />
                  <span>{deactivationReasonError}</span>
                </div>
              )}
            </div>
          </div>

          {/* Password field */}
          <div className="deactivate-password-field">
            <div className="password-input-wrapper">
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t.currentPassword}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  className={`password-input ${error ? "error" : ""}`}
                />
                <button
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  type="button"
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              {/* Error message */}
              {error && (
                <div className="password-error-message">
                  <FiAlertTriangle />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="deactivate-modal-actions deactivate-security-modal-actions">
          <button className="deactivate-modal-btn cancel" onClick={onClose}>
            <span className="btn-text">{t.cancel}</span>
          </button>
          <button
            className="deactivate-modal-btn confirm"
            onClick={handleConfirm}
            disabled={!password || !deactivationReason.trim() || deactivationReasonError || isLoading}
          >
            <span className="btn-text">{isLoading ? t.verifying : t.continue}</span>
            {isLoading && <div className="btn-spinner" />}
          </button>
        </div>

        {/* Footer info */}
        <div className="deactivate-modal-footer">
          <div className="footer-info">
            <span>{t.yourPasswordEncrypted}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Deactivate (OTP) ----------------

function DeactivateOtpModal({ onClose, showBanner, navigate, t }) {
  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [otpExpired, setOtpExpired] = useState(false);
  const [resendText, setResendText] = useState(t.resendOtp);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);
  const inputsRef = useRef([]);
  const otpSentRef = useRef(false);
  const [verificationMethod, setVerificationMethod] = useState("email");
  const [hasSmsBeenSent, setHasSmsBeenSent] = useState(false);
  const [userPhone, setUserPhone] = useState(null);
  const [timerActive, setTimerActive] = useState(true);

  const API_BASE =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5030/auravit-bb843/us-central1"
      : "https://us-central1-auravit-bb843.cloudfunctions.net";

  const normalizePhoneNumber = (phone) => {
    if (!phone) return null;
    const cleaned = String(phone).replace(/\D/g, '');
    
    if (cleaned.length === 10 && cleaned.startsWith('9')) {
      return `+63${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
      return `+63${cleaned.slice(1)}`;
    } else if (cleaned.length === 12 && cleaned.startsWith('63')) {
      return `+${cleaned}`;
    } else if (phone.startsWith('+63')) {
      return phone;
    }
    return null;
  };

  // Get user's phone number
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const rawPhone = storedUser.phone || storedUser.contact || (storedUser.phoneLast10 ? `+63${storedUser.phoneLast10}` : null);
    const phone = normalizePhoneNumber(rawPhone);
    setUserPhone(phone);
  }, []);

  // Send OTP on mount
  useEffect(() => {
    if (otpSentRef.current) return;

    const timer = setTimeout(async () => {
      otpSentRef.current = true;
      const pendingDeactivation = JSON.parse(
        localStorage.getItem('pendingDeactivation') || 'null'
      );

      if (!pendingDeactivation?.email) {
        setError(t.emailNotFoundPleaseTryAgain);
        return;
      }

      try {
        setIsSendingOtp(true);
        const { generateOtpCode, sendOtpEmail } = await import('../../services/otpService');
        const otpCode = generateOtpCode();
        const requestId = `deactivate-${Date.now()}`;
        const expiresAt = Date.now() + 60000;

        await sendOtpEmail({
          email: pendingDeactivation.email,
          otp: otpCode,
          purpose: 'account-deactivation',
          requestId,
          expiresAt,
        });

        setOtpChallenge({
          otp: otpCode,
          requestId,
          expiresAt,
        });
        setError("");
      } catch (err) {
        console.error('Failed to send OTP:', err);
        setError(t.failedToSendVerificationCode);
      } finally {
        setIsSendingOtp(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!timerActive || timeLeft <= 0) {
      if (timeLeft <= 0) {
        setOtpExpired(true);
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, timerActive]);

  const resetOtpExpiration = () => {
    setOtpExpired(false);
    setTimeLeft(60);
    setTimerActive(true);
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60);
    const seconds = sec % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleTryAnotherWay = () => {
    setVerificationMethod("sms");
    setHasSmsBeenSent(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimeLeft(60);
    setOtpExpired(false);
    setTimerActive(false);
  };

  const handleSendSms = async () => {
    if (isSendingOtp || !userPhone) return;

    try {
      setIsSendingOtp(true);
      const { generateOtpCode, sendOtpSms } = await import('../../services/otpService');
      const otpCode = generateOtpCode();
      const requestId = `deactivate-sms-${Date.now()}`;
      const expiresAt = Date.now() + 60000;

      await sendOtpSms({
        phone: userPhone,
        otp: otpCode,
        purpose: 'account-deactivation',
        requestId,
        expiresAt,
      });

      setOtpChallenge({
        otp: otpCode,
        requestId,
        expiresAt,
      });
      setError("");
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeLeft(60);
      setOtpExpired(false);
      setTimerActive(true);
      setHasSmsBeenSent(true);
    } catch (err) {
      console.error('Failed to send OTP SMS:', err);
      setError(t.failedToSendVerificationCode);
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Input change
  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return; // only digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  // Backspace
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // Paste OTP
  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").slice(0, OTP_LENGTH);
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("");
      setOtp(pasteArray);
      const lastFilled = pasteArray.length - 1;
      inputsRef.current[lastFilled].focus();
    }
    e.preventDefault();
  };

  // Verify OTP
  const handleVerify = async () => {
    if (!isOtpComplete) {
      setError(t.pleaseEnterFull6DigitCode);
      return;
    }

    if (otpExpired) {
      setError(t.codeHasExpiredPleaseRequestNew);
      return;
    }

    setIsVerifying(true);
    try {
      const pendingDeactivation = JSON.parse(
        localStorage.getItem('pendingDeactivation') || 'null'
      );

      if (!pendingDeactivation?.uid) {
        setError(t.noPendingDeactivationFound);
        return;
      }

      if (otp.join("") !== otpChallenge?.otp) {
        setError(t.incorrectCodePleaseTryAgain);
        return;
      }

      const authUser = auth.currentUser;
      if (!authUser) {
        setError(t.sessionExpiredPleaseLogIn);
        return;
      }

      // Update Firestore: tag as deactivated
      await updateDoc(doc(db, "users", authUser.uid), {
        status: "deactivated",
        deactivatedAt: serverTimestamp(),
        deactivationReason: pendingDeactivation.deactivationReason || "",
        twoFactorEnabled: true, // Force 2FA to require OTP on reactivation
      });

      // Send deactivation email
      try {
        const deactivationDate = new Date().toLocaleDateString();
        
        // Firestore optimization: Use cached user data instead of re-fetching
        // Priority: shared cache → pendingDeactivation data → fallback
        let firstName, lastName;
        
        if (sharedUserDataCacheRef.current) {
          firstName = sharedUserDataCacheRef.current?.personalInformation?.firstName || 
                     sharedUserDataCacheRef.current?.firstName || "";
          lastName = sharedUserDataCacheRef.current?.personalInformation?.lastName || 
                    sharedUserDataCacheRef.current?.lastName || "";
        } else {
          // Fallback to pendingDeactivation data
          firstName = pendingDeactivation.firstName || "";
          lastName = pendingDeactivation.lastName || "";
        }
        
        const userName = firstName && lastName ? `${firstName} ${lastName}` :
          (firstName || lastName || pendingDeactivation.userName || "User");

        await fetch(`${API_BASE}/sendUserDeactivationEmail`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userName,
            deactivationDate,
            reason: pendingDeactivation.deactivationReason,
            userEmail: pendingDeactivation.email,
          }),
        });
      } catch (emailError) {
        console.error("Failed to send user deactivation email:", emailError);
        // Don't fail the whole process if email fails
      }

      // Clear pending data
      localStorage.removeItem('pendingDeactivation');

      showBanner?.("Account deactivated successfully.", "success");

      // Log out and redirect
      localStorage.clear();
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error('Deactivation error:', err);
      setError(err?.message || t.deactivationFailedPleaseTryAgain);
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (resendText === t.otpSent || isSendingOtp) return;

    try {
      setIsSendingOtp(true);
      const pendingDeactivation = JSON.parse(
        localStorage.getItem('pendingDeactivation') || 'null'
      );

      if (verificationMethod === "email") {
        if (!pendingDeactivation?.email) {
          setError(t.emailNotFoundPleaseTryAgain);
          return;
        }

        const { generateOtpCode, sendOtpEmail } = await import('../../services/otpService');
        const otpCode = generateOtpCode();
        const requestId = `deactivate-${Date.now()}`;
        const expiresAt = Date.now() + 60000;

        await sendOtpEmail({
          email: pendingDeactivation.email,
          otp: otpCode,
          purpose: 'account-deactivation',
          requestId,
          expiresAt,
        });

        setOtpChallenge({
          otp: otpCode,
          requestId,
          expiresAt,
        });
      } else if (verificationMethod === "sms" && userPhone) {
        const { generateOtpCode, sendOtpSms } = await import('../../services/otpService');
        const otpCode = generateOtpCode();
        const requestId = `deactivate-sms-${Date.now()}`;
        const expiresAt = Date.now() + 60000;

        await sendOtpSms({
          phone: userPhone,
          otp: otpCode,
          purpose: 'account-deactivation',
          requestId,
          expiresAt,
        });

        setOtpChallenge({
          otp: otpCode,
          requestId,
          expiresAt,
        });
      }

      setError("");
      setOtp(Array(OTP_LENGTH).fill(""));
      resetOtpExpiration();
      setResendText(t.otpSent);
      if (verificationMethod === "sms") {
        setHasSmsBeenSent(true);
      }
      setTimeout(() => setResendText(t.resendOtp), 3000);
    } catch (err) {
      console.error("Resend OTP error", err);
      setError(t.failedToResendCode);
    } finally {
      setIsSendingOtp(false);
    }
  };

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="deactivate-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="deactivate-modal-close" onClick={onClose}>
          <FiX />
        </button>

        {/* Scrollable content wrapper */}
        <div className="deactivate-modal-content-wrapper">
          {/* Email/Phone info */}
          <div className="deactivate-email-info">
            <div className="email-info-icon">
              <FiMail />
            </div>
            <div className="email-info-content">
              <h4 style={{ color: '#000000' }}>{t.verificationSent}</h4>
              <p>Enter 6-digit code we sent to: {(() => {
                if (verificationMethod === "sms") {
                  return userPhone;
                }
                const pending = JSON.parse(localStorage.getItem('pendingDeactivation') || '{}');
                return pending.email;
              })()}</p>
            </div>
          </div>

          {/* OTP inputs */}
          <div className="deactivate-otp-container">
            <div className={`deactivate-otp-inputs ${error ? "shake" : ""}`}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  className={`deactivate-otp-input ${error ? "error" : ""}`}
                  disabled={isVerifying}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {/* Error message */}
            {error && (
              <div className="password-error-message">
                <FiAlertTriangle />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Timer and resend */}
          <div className="deactivate-otp-timer">
            {timerActive && (
              <div className="timer-display">
                <FiClock />
                <span>{t.codeExpiresIn} <strong>{formatTime(timeLeft)}</strong></span>
              </div>
            )}
            
            <div className="resend-container" style={{ display: 'flex', alignItems: 'center', gap: '2px', flexWrap: 'wrap' }}>
              {verificationMethod === "email" ? (
                <>
                  <span className="resend-text">{t.didntReceiveCode}</span>
                  <button 
                    className={`resend-btn ${otpExpired ? "enabled" : ""}`}
                    onClick={handleResend}
                    disabled={resendText === t.otpSent || isSendingOtp}
                  >
                    {isSendingOtp ? (
                      <>
                        <div className="btn-spinner" />
                        {t.sending}
                      </>
                    ) : (
                      resendText
                    )}
                  </button>
                </>
              ) : (
                <>
                  {hasSmsBeenSent ? (
                    <>
                      <span className="resend-text">{t.didntReceiveCode}</span>
                      <button 
                        className={`resend-btn ${otpExpired ? "enabled" : ""}`}
                        onClick={handleResend}
                        disabled={resendText === t.otpSent || isSendingOtp}
                      >
                        {isSendingOtp ? (
                          <>
                            <div className="btn-spinner" />
                            {t.sending}
                          </>
                        ) : (
                          resendText
                        )}
                      </button>
                    </>
                  ) : (
                    <button 
                      className="resend-btn"
                      onClick={handleSendSms}
                      disabled={isSendingOtp}
                      style={{ textDecoration: 'underline' }}
                    >
                      {isSendingOtp ? (
                        <>
                          <div className="btn-spinner" />
                          {t.sending}
                        </>
                      ) : (
                        "Send SMS"
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Try another way button */}
          {verificationMethod === "email" && userPhone && (
            <div style={{ textAlign: 'center', marginTop: '-30px', marginBottom: '20px' }}>
              <button
                onClick={handleTryAnotherWay}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                Try another way
              </button>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="deactivate-modal-actions">
          <button className="deactivate-modal-btn cancel" onClick={onClose}>
            <span className="btn-text">{t.cancel}</span>
          </button>
          <button
            className="deactivate-modal-btn confirm"
            onClick={handleVerify}
            disabled={isVerifying || !isOtpComplete || otpExpired || isSendingOtp}
          >
            <span className="btn-text">
              {isVerifying ? (
                <>
                  <div className="btn-spinner" />
                  {t.confirming}
                </>
              ) : (
                t.confirmDeactivation
              )}
            </span>          
            </button>
        </div>

        {/* Footer info */}
        <div className="deactivate-modal-footer">
          <div className="footer-info">
            <span>{t.makeSureCheckSpam}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Delete Account ----------------

// Step 1: Warning Modal
function DeleteAccountModal({ onClose, onNext, t }) {
  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="delete-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="delete-modal-close" onClick={onClose}>
          <FiX />
        </button>

        {/* Scrollable content wrapper */}
        <div className="delete-modal-content-wrapper">
          {/* Warning icon with animated background */}
          <div className="delete-modal-icon-wrapper">
            <div className="delete-modal-icon-bg">
              <FiAlertTriangle />
            </div>
          </div>

          {/* Content */}
          <div className="delete-modal-content">
            <h2 className="delete-modal-title">Delete Account</h2>
            <p className="delete-modal-subtext">
              Deleting your account is permanent and cannot be undone. Please read the information below carefully before proceeding.
            </p>
          </div>

          {/* Impact cards */}
          <div className="delete-modal-impacts">
            <div className="impact-card">
              <div className="impact-icon">
                <FiUserX />
              </div>
              <div className="impact-content">
                <h4>Permanent Removal</h4>
                <p>Your account will be permanently removed and cannot be recovered.</p>
              </div>
            </div>
            
            <div className="impact-card">
              <div className="impact-icon">
                <FiTrash2 />
              </div>
              <div className="impact-content">
                <h4>Data Deletion</h4>
                <p>All personal data not required for legal purposes will be permanently deleted.</p>
              </div>
            </div>
            
            <div className="impact-card">
              <div className="impact-icon">
                <FiXCircle />
              </div>
              <div className="impact-content">
                <h4>No Access</h4>
                <p>You will no longer be able to access your account or any associated services.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="delete-modal-actions">
          <button className="delete-modal-btn cancel" onClick={onClose}>
            <span className="btn-text">Cancel</span>
          </button>
          <button className="delete-modal-btn confirm" onClick={onNext}>
            <span className="btn-text">Continue</span>
            <FiAlertTriangle className="btn-icon" />
          </button>
        </div>

        {/* Footer info */}
        <div className="delete-modal-footer">
          <div className="footer-info">
            <FiAlertTriangle className="info-icon" />
            <span>This action is irreversible. All progress will be lost.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 2: Password Modal
function DeletePasswordModal({ onClose, onNext, password, setPassword, passwordError, setPasswordError, deletingAccount, t }) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="delete-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="delete-modal-close" onClick={onClose}>
          <FiX />
        </button>

        {/* Scrollable content wrapper */}
        <div className="delete-modal-content-wrapper">
          {/* Icon with animated background */}
          <div className="delete-modal-icon-wrapper">
            <div className="delete-modal-icon-bg delete-password-icon-bg">
              <FiLock />
            </div>
          </div>

          {/* Content */}
          <div className="delete-modal-content">
            <h2 className="delete-modal-title">Enter Password</h2>
            <p className="delete-modal-subtext">
              Please enter your password to confirm account deletion.
            </p>

            {/* Security notice */}
            <div className="delete-security-notice">
              <div className="security-notice-icon">
                <FiShield />
              </div>
              <div className="security-notice-content">
                <h4>Security Verification</h4>
                <p>This ensures that only you can delete your account.</p>
              </div>
            </div>

            {/* Password field */}
            <div className="delete-password-field">
              <div className="password-input-wrapper">
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError("");
                    }}
                    placeholder="Password"
                    className={`password-input ${passwordError ? "error" : ""}`}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {passwordError && (
                  <div className="password-error-message">
                    <FiAlertTriangle />
                    <span>{passwordError}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="delete-modal-actions">
          <button className="delete-modal-btn cancel" onClick={onClose} disabled={deletingAccount}>
            <span className="btn-text">Cancel</span>
          </button>
          <button className="delete-modal-btn confirm" onClick={onNext} disabled={deletingAccount}>
            {deletingAccount ? (
              <span className="btn-text">Verifying...</span>
            ) : (
              <span className="btn-text">Continue</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Step 3: OTP Modal
function DeleteOtpModal({ 
  onClose, 
  onNext, 
  otpCode, 
  setOtpCode, 
  otpError, 
  showOtpError, 
  verificationMethod, 
  userPhone, 
  hasSmsBeenSent, 
  resendLoading, 
  emailSecondsLeft, 
  smsSecondsLeft, 
  onResendOtp, 
  onTryAnotherWay, 
  onSendSms, 
  onOtpChange, 
  onKeyPress, 
  deletingAccount,
  t 
}) {
  const authUser = auth.currentUser;

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="delete-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="delete-modal-close" onClick={onClose}>
          <FiX />
        </button>

        {/* Scrollable content wrapper */}
        <div className="delete-modal-content-wrapper">
          {/* Icon with animated background */}
          <div className="delete-modal-icon-wrapper">
            <div className="delete-modal-icon-bg delete-otp-icon-bg">
              <FiKey />
            </div>
          </div>

          {/* Content */}
          <div className="delete-modal-content">
            <h2 className="delete-modal-title">Verify OTP</h2>
            <p className="delete-modal-subtext">
              Enter the 6-digit code sent to {verificationMethod === "sms" ? userPhone : authUser?.email}
            </p>

            {/* Email/Phone info */}
            <div className="delete-email-info">
              <div className="email-info-icon">
                {verificationMethod === "sms" ? <FiPhone /> : <FiMail />}
              </div>
              <div className="email-info-content">
                <h4>Code Sent</h4>
                <p>Please check your {verificationMethod === "sms" ? "SMS messages" : "email inbox"} for the verification code.</p>
              </div>
            </div>

            {/* OTP inputs */}
            <div className="delete-otp-container">
              <div className={`delete-otp-inputs ${otpError && showOtpError ? "error" : ""}`}>
                {otpCode.map((digit, index) => (
                  <input
                    key={index}
                    id={`delete-otp-${index}`}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => onOtpChange(e.target.value, index)}
                    onKeyDown={(e) => onKeyPress(e, index)}
                    className={`delete-otp-input ${otpError && showOtpError ? "error" : ""}`}
                    disabled={deletingAccount}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              {/* Error message */}
              {otpError && showOtpError && (
                <div className="password-error-message">
                  <FiAlertTriangle />
                  <span>Incorrect OTP</span>
                </div>
              )}
            </div>

            {/* Timer and Resend */}
            <div className="delete-otp-timer">
              <div className="timer-display">
                <FiClock />
                <span>Code expires in:</span>
                <strong>
                  {Math.floor((verificationMethod === "email" ? emailSecondsLeft : smsSecondsLeft) / 60)}:
                  {String((verificationMethod === "email" ? emailSecondsLeft : smsSecondsLeft) % 60).padStart(2, '0')}
                </strong>
              </div>
              <div className="resend-container">
                <button
                  onClick={onResendOtp}
                  disabled={(verificationMethod === "email" ? emailSecondsLeft : smsSecondsLeft) > 0 || resendLoading}
                  className={`delete-resend-btn ${((verificationMethod === "email" ? emailSecondsLeft : smsSecondsLeft) > 0 || resendLoading) ? "disabled" : "enabled"}`}
                >
                  {resendLoading ? <FiRefreshCw /> : <FiSend />}
                  {resendLoading ? " Sending..." : ((verificationMethod === "email" ? emailSecondsLeft : smsSecondsLeft) > 0 
                    ? ` Resend in ${verificationMethod === "email" ? emailSecondsLeft : smsSecondsLeft}s` 
                    : " Resend Code")}
                </button>
              </div>
            </div>

            {/* Try Another Way button */}
            {verificationMethod === "email" && userPhone && (
              <button 
                onClick={onTryAnotherWay}
                className="delete-try-another-way-btn"
              >
                Try another way
              </button>
            )}

            {/* Send SMS button */}
            {verificationMethod === "sms" && !hasSmsBeenSent && (
              <button 
                onClick={onSendSms}
                className="delete-send-sms-btn"
                disabled={resendLoading}
              >
                {resendLoading ? "Sending..." : "Send SMS"}
              </button>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="delete-modal-actions">
          <button className="delete-modal-btn cancel" onClick={onClose} disabled={deletingAccount}>
            <span className="btn-text">Cancel</span>
          </button>
          <button className="delete-modal-btn confirm" onClick={onNext} disabled={deletingAccount}>
            {deletingAccount ? (
              <span className="btn-spinner" />
            ) : (
              <FiAlertTriangle className="btn-icon" />
            )}
            <span className="btn-text">{deletingAccount ? "Deleting..." : "Delete Account"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- UserProfile ----------------
function UserProfile({ initialItem = "account", initialPrivacyTab = "pending" }) {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null);
  const [activeItem, setActiveItem] = useState(initialItem);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    setActiveItem(initialItem);
  }, [initialItem]);

  const translatedMenuItems = menuItems.map(item => ({
    ...item,
    label: t[item.id] || item.label
  }));

  // Close sidebar when clicking a menu item on mobile
  const handleMenuItemClick = (item) => {
    if (item.id === "logout") {
      // Close sidebar first, then open logout modal
      setSidebarOpen(false);
      setActiveModal("logout");
    } else if (item.id === "deactivate") {
      // Close sidebar first, then open deactivate modal
      setSidebarOpen(false);
      setActiveModal("deactivate-step1");
    } else {
      setActiveItem(item.id);
      // Close sidebar on mobile after navigation
      setSidebarOpen(false);
    }
  };

  // Close sidebar on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    if (sidebarOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [sidebarOpen]);

  // Modal Banner State
  const [banner, setBanner] = useState({ show: false, message: "", type: "" });

  // Delete Account State
  const [deleteStep, setDeleteStep] = useState(1); // 1: warning, 2: password, 3: otp
  const [deletePassword, setDeletePassword] = useState("");
  const [deletePasswordError, setDeletePasswordError] = useState("");
  const [deleteOtpCode, setDeleteOtpCode] = useState(["", "", "", "", "", ""]);
  const [deleteOtpError, setDeleteOtpError] = useState(false);
  const [deleteShowOtpError, setDeleteShowOtpError] = useState(false);
  const [deleteVerificationMethod, setDeleteVerificationMethod] = useState("email");
  const [deleteUserPhone, setDeleteUserPhone] = useState(null);
  const [deleteHasSmsBeenSent, setDeleteHasSmsBeenSent] = useState(false);
  const [deleteResendLoading, setDeleteResendLoading] = useState(false);
  const [deleteEmailSecondsLeft, setDeleteEmailSecondsLeft] = useState(60);
  const [deleteSmsSecondsLeft, setDeleteSmsSecondsLeft] = useState(0);
  const [deleteOtpRequestId, setDeleteOtpRequestId] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Two-Factor Authentication State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorOtpModal, setShowTwoFactorOtpModal] = useState(false);
  const [pendingTwoFactorValue, setPendingTwoFactorValue] = useState(false);
  const [twoFactorUserEmail, setTwoFactorUserEmail] = useState('');
  const [twoFactorUserPhone, setTwoFactorUserPhone] = useState('');
  const [twoFactorUserFirstName, setTwoFactorUserFirstName] = useState('');

  const showBanner = (message, type = "success") => {
    setBanner({ show: true, message, type });
    setTimeout(() => setBanner({ show: false, message: "", type: "" }), 3000);
  };

  // Delete account timers
  useEffect(() => {
    if (deleteEmailSecondsLeft <= 0) return;
    const t = setTimeout(() => setDeleteEmailSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [deleteEmailSecondsLeft]);

  useEffect(() => {
    if (deleteSmsSecondsLeft <= 0) return;
    const t = setTimeout(() => setDeleteSmsSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [deleteSmsSecondsLeft]);

  // Fetch user phone number for delete account OTP
  useEffect(() => {
    const fetchDeleteUserPhone = async () => {
      const authUser = auth.currentUser;
      if (!authUser?.email) return;
      try {
        const userData = await userService.getUserByEmail(authUser.email);
        const rawPhone = userData?.phone || userData?.contact || userData?.contactDetails?.contact || (userData?.phoneLast10 ? `+63${userData.phoneLast10}` : null);
        
        if (rawPhone) {
          const cleaned = String(rawPhone).replace(/\D/g, '');
          let normalizedPhone = null;
          
          if (cleaned.length === 10 && cleaned.startsWith('9')) {
            normalizedPhone = `+63${cleaned}`;
          } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
            normalizedPhone = `+63${cleaned.slice(1)}`;
          } else if (cleaned.length === 12 && cleaned.startsWith('63')) {
            normalizedPhone = `+${cleaned}`;
          } else if (rawPhone.startsWith('+63')) {
            normalizedPhone = rawPhone;
          }
          
          setDeleteUserPhone(normalizedPhone);
        }
      } catch (error) {
        console.error('Error fetching user phone for delete:', error);
      }
    };
    
    fetchDeleteUserPhone();
  }, []);

  // Fetch user email and phone for 2FA modal fallback
  useEffect(() => {
    const fetchTwoFactorUserData = async () => {
      const authUser = auth.currentUser;
      if (!authUser?.email) return;
      try {
        const userData = await userService.getUserByEmail(authUser.email);
        setTwoFactorUserEmail(userData?.email || authUser.email || '');
        setTwoFactorUserFirstName(
          userData?.personalInformation?.firstName ||
          userData?.firstName ||
          ''
        );
        
        const possibleContactFields = [
          'contact', 'contactNumber', 'phone', 'phoneLast10', 'phoneNumber',
          'mobile', 'mobileNumber', 'cellphone', 'cell'
        ];
        let rawPhone = null;
        for (const field of possibleContactFields) {
          if (userData && userData[field]) {
            rawPhone = userData[field];
            break;
          }
        }
        // Also check nested contactDetails.contact
        if (!rawPhone && userData?.contactDetails?.contact) {
          rawPhone = userData.contactDetails.contact;
        }
        
        if (rawPhone) {
          const cleaned = String(rawPhone).replace(/\D/g, '');
          let normalizedPhone = null;
          if (cleaned.length === 10 && cleaned.startsWith('9')) {
            normalizedPhone = `+63${cleaned}`;
          } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
            normalizedPhone = `+63${cleaned.slice(1)}`;
          } else if (cleaned.length === 12 && cleaned.startsWith('63')) {
            normalizedPhone = `+${cleaned}`;
          } else if (String(rawPhone).startsWith('+63')) {
            normalizedPhone = rawPhone;
          }
          setTwoFactorUserPhone(normalizedPhone || String(rawPhone));
        }
      } catch (error) {
        console.error('Error fetching user data for 2FA:', error);
      }
    };
    
    fetchTwoFactorUserData();
  }, []);

  // Handle delete account - multi-step process
  const handleDeleteAccount = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      setActiveModal("delete-step2");
    } else if (deleteStep === 2) {
      await verifyPasswordAndSendOtp();
    } else if (deleteStep === 3) {
      await verifyOtpAndDelete();
    }
  };

  const verifyPasswordAndSendOtp = async () => {
    if (!deletePassword) {
      setDeletePasswordError("Please enter your password");
      return;
    }

    setDeletingAccount(true);
    setDeletePasswordError("");
    try {
      const authUser = auth.currentUser;
      if (!authUser) {
        setDeletePasswordError("No authenticated user found");
        return;
      }

      // Re-authenticate with password
      const credential = EmailAuthProvider.credential(authUser.email, deletePassword);
      await reauthenticateWithCredential(authUser, credential);
      
      // Generate and send OTP
      const { generateOtpCode, sendOtpEmail, sendOtpSms } = await import('../../services/otpService');
      const otp = generateOtpCode();
      const requestId = `delete-${Date.now()}`;
      const expiresAt = Date.now() + 60000;

      setDeleteOtpRequestId(requestId);

      if (deleteVerificationMethod === "email") {
        await sendOtpEmail({
          email: authUser.email,
          otp,
          purpose: "account_deletion",
          requestId,
          expiresAt,
        });
        setDeleteEmailSecondsLeft(60);
      } else if (deleteVerificationMethod === "sms" && deleteUserPhone) {
        await sendOtpSms({
          phone: deleteUserPhone,
          otp,
          purpose: "account_deletion",
          requestId,
          expiresAt,
        });
        setDeleteHasSmsBeenSent(true);
        setDeleteSmsSecondsLeft(60);
      }

      // Store OTP in localStorage
      localStorage.setItem(requestId, JSON.stringify({ otp, expiresAt }));

      setDeleteStep(3);
      setActiveModal("delete-step3");
      setDeletePassword("");
    } catch (error) {
      if (!error?.message?.toLowerCase().includes('invalid credential')) {
        console.error('Error verifying password:', error);
      }
      setDeletePasswordError("Incorrect password. Please try again.");
    } finally {
      setDeletingAccount(false);
    }
  };

  const verifyOtpAndDelete = async () => {
    const otp = deleteOtpCode.join("");
    if (otp.length !== 6) {
      setDeleteOtpError(true);
      setDeleteShowOtpError(true);
      return;
    }

    setDeletingAccount(true);
    try {
      // Verify OTP
      const storedData = localStorage.getItem(deleteOtpRequestId);
      if (!storedData) {
        throw new Error('OTP expired or invalid');
      }

      const { otp: storedOtp, expiresAt } = JSON.parse(storedData);
      if (Date.now() > expiresAt) {
        throw new Error('OTP expired');
      }

      if (otp !== storedOtp) {
        throw new Error('Invalid OTP');
      }

      // OTP verified - proceed with deletion
      const authUser = auth.currentUser;
      if (!authUser) {
        throw new Error('No authenticated user found');
      }

      // Delete user document from Firestore
      const userDocRef = doc(db, 'users', authUser.uid);
      await deleteDoc(userDocRef);

      // Delete user from Firebase Auth
      await authUser.delete();

      // Clear stored OTP
      localStorage.removeItem(deleteOtpRequestId);

      // Navigate to login
      navigate('/login');
      showBanner("Account deleted successfully", "success");
    } catch (error) {
      console.error('Error verifying OTP or deleting account:', error);
      setDeleteOtpError(true);
      setDeleteShowOtpError(true);
      setDeleteOtpCode(["", "", "", "", "", ""]);
      showBanner(error.message || 'Failed to delete account. Please try again.', "error");
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleDeleteResendOtp = async () => {
    const currentSecondsLeft = deleteVerificationMethod === "email" ? deleteEmailSecondsLeft : deleteSmsSecondsLeft;
    
    if (currentSecondsLeft > 0) return;
    if (deleteResendLoading) return;

    setDeleteResendLoading(true);
    setDeleteOtpError(false);
    setDeleteShowOtpError(false);

    try {
      const authUser = auth.currentUser;
      if (!authUser) throw new Error("No authenticated user found");

      const { generateOtpCode, sendOtpEmail, sendOtpSms } = await import('../../services/otpService');
      const otp = generateOtpCode();
      const requestId = `delete-${Date.now()}`;
      const expiresAt = Date.now() + 60000;

      setDeleteOtpRequestId(requestId);

      if (deleteVerificationMethod === "email") {
        await sendOtpEmail({
          email: authUser.email,
          otp,
          purpose: "account_deletion",
          requestId,
          expiresAt,
        });
        setDeleteEmailSecondsLeft(60);
      } else if (deleteVerificationMethod === "sms" && deleteUserPhone) {
        await sendOtpSms({
          phone: deleteUserPhone,
          otp,
          purpose: "account_deletion",
          requestId,
          expiresAt,
        });
        setDeleteHasSmsBeenSent(true);
        setDeleteSmsSecondsLeft(60);
      }

      localStorage.setItem(requestId, JSON.stringify({ otp, expiresAt }));
      showBanner(deleteVerificationMethod === "email" 
        ? "Please check your email for the new OTP." 
        : "Please check your phone for the new OTP.", "success");
    } catch (error) {
      console.error('Resend OTP error:', error);
      showBanner(error?.message || "Failed to resend OTP.", "error");
    } finally {
      setDeleteResendLoading(false);
    }
  };

  const handleDeleteTryAnotherWay = () => {
    setDeleteVerificationMethod("sms");
    setDeleteHasSmsBeenSent(false);
    setDeleteOtpCode(["", "", "", "", "", ""]);
    setDeleteOtpError(false);
    setDeleteShowOtpError(false);
  };

  const handleDeleteSendSms = async () => {
    try {
      setDeleteResendLoading(true);
      setDeleteOtpError(false);
      setDeleteShowOtpError(false);
      
      const authUser = auth.currentUser;
      if (!authUser) throw new Error("No authenticated user found");

      const { generateOtpCode, sendOtpSms } = await import('../../services/otpService');
      const otp = generateOtpCode();
      const requestId = `delete-${Date.now()}`;
      const expiresAt = Date.now() + 60000;

      setDeleteOtpRequestId(requestId);

      await sendOtpSms({
        phone: deleteUserPhone,
        otp,
        purpose: "account_deletion",
        requestId,
        expiresAt,
      });
      
      setDeleteHasSmsBeenSent(true);
      setDeleteSmsSecondsLeft(60);
      localStorage.setItem(requestId, JSON.stringify({ otp, expiresAt }));
    } catch (error) {
      console.error('Error sending SMS:', error);
      showBanner(error?.message || "Failed to send SMS. Please try again.", "error");
    } finally {
      setDeleteResendLoading(false);
    }
  };

  const handleDeleteOtpChange = (raw, index) => {
    const digits = String(raw || "").replace(/\D/g, "");
    
    if (!digits) {
      setDeleteOtpCode((prev) => {
        const next = [...prev];
        next[index] = "";
        return next;
      });
      return;
    }

    // Full paste (or multi-char input)
    if (digits.length > 1) {
      const arr = digits.slice(0, 6).split("");
      setDeleteOtpCode((prev) => {
        const next = ["", "", "", "", "", ""];
        for (let i = 0; i < arr.length; i++) next[i] = arr[i];
        return next;
      });
      setDeleteShowOtpError(false);
      setDeleteOtpError(false);
      return;
    }

    // Single digit input
    setDeleteOtpCode((prev) => {
      const next = [...prev];
      next[index] = digits[0];
      return next;
    });
    setDeleteShowOtpError(false);
    setDeleteOtpError(false);
    
    // Auto-focus next input
    if (index < 5) {
      const nextInput = document.getElementById(`delete-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleDeleteKeyPress = (e, index) => {
    if (e.key !== "Backspace") return;
    if (deleteOtpCode[index]) {
      setDeleteOtpCode((prev) => {
        const next = [...prev];
        next[index] = "";
        return next;
      });
      return;
    }
    if (index > 0) {
      setDeleteOtpCode((prev) => {
        const next = [...prev];
        next[index - 1] = "";
        return next;
      });
      const prevInput = document.getElementById(`delete-otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const closeDeleteModal = async () => {
    setActiveModal(null);
    setDeleteStep(1);
    setDeletePassword("");
    setDeleteOtpCode(["", "", "", "", "", ""]);
    setDeleteOtpError(false);
    setDeleteShowOtpError(false);
    setDeleteVerificationMethod("email");
    setDeleteHasSmsBeenSent(false);
    setDeleteEmailSecondsLeft(60);
    setDeleteSmsSecondsLeft(0);
    if (deleteOtpRequestId) {
      localStorage.removeItem(deleteOtpRequestId);
    }
    setDeleteOtpRequestId(null);
  };

  // Two-Factor Authentication Handlers
  const updateTwoFactor = async (nextValue) => {
    setPendingTwoFactorValue(nextValue);
    setShowTwoFactorOtpModal(true);
  };

  const handleTwoFactorOtpSuccess = async (verifiedValue) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      await updateDoc(doc(db, "users", user.uid), { 
        twoFactorEnabled: verifiedValue, 
        updatedAt: serverTimestamp() 
      });
      
      // Update local storage to keep user data in sync
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (storedUser.uid === user.uid) {
        localStorage.setItem("user", JSON.stringify({ ...storedUser, twoFactorEnabled: verifiedValue }));
      }
      
      setTwoFactorEnabled(verifiedValue);
    } catch (err) {
      console.error("Error updating 2FA:", err);
    }
  };

  const renderPanel = () => {
    switch (activeItem) {
      case "account":
        return <AccountPanel navigate={navigate} showBanner={showBanner} twoFactorEnabled={twoFactorEnabled} setTwoFactorEnabled={setTwoFactorEnabled} updateTwoFactor={updateTwoFactor} />;
      case "notifications":
        return <ProfileNotifications sharedUserDataCacheRef={sharedUserDataCacheRef} />;
      case "accessibility":
        return <ProfileAccessibility />;
      case "content":
        return <ProfileContent sharedUserDataCacheRef={sharedUserDataCacheRef} />;
      case "privacy":
        return (
          <DataPrivacyPanel
            showBanner={showBanner}
            initialTab={initialPrivacyTab}
          />
        );

      default:
        return (
          <div className="panel-placeholder">
            {menuItems.find((i) => i.id === activeItem)?.label}
          </div>
        );
    }
  };



  return (
    <div className="admin-profile">
      {/* Mobile Settings Toggle */}
      <button 
        className="settings-toggle-btn" 
        onClick={() => setSidebarOpen(true)}
        aria-label="Open Settings"
      >
        <FiSliders />
        <span>Settings</span>
      </button>

      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div 
          className="settings-backdrop" 
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="admin-content-wrapper">
        <aside className={`admin-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <div className="admin-sidebar-header">
            <div className="admin-settings-title-wrapper">
              <FiSliders className="admin-settings-icon" />
              <h2 className="admin-settings-title">Settings</h2>
            </div>
            <button 
              className="sidebar-close-btn" 
              onClick={() => setSidebarOpen(false)}
              aria-label="Close Settings"
            >
              <FiX />
            </button>
          </div>
          <div className="admin-menu">
            {translatedMenuItems.map((item) => (
              <button
                key={item.id}
                className={`admin-menu-button
        ${activeItem === item.id ? "active" : ""}
        ${item.id === "logout" ? "logout" : ""}
        ${item.id === "deactivate" ? "deactivate" : ""}
      `}
                onClick={() => handleMenuItemClick(item)}
              >
                <span className="admin-icon">{item.icon}</span>
                <span className="admin-text">{item.label}</span>
              </button>
            ))}
            
            {/* Danger Zone Section */}
            <div className="admin-menu-danger-zone">
              <div className="danger-zone-spacer"></div>
              <div className="danger-zone-label">Danger Zone</div>
              <button className="admin-menu-button danger-zone-delete" onClick={() => {
                // Close sidebar first, then open delete modal
                setSidebarOpen(false);
                setActiveModal("delete-step1");
              }}>
                <span className="admin-icon"><FiTrash2 /></span>
                <span className="admin-text">Delete Account</span>
              </button>
            </div>
          </div>
        </aside>

        <main className="admin-panel">
          <div className="admin-panel-content scrollable">{renderPanel()}</div>
        </main>
      </div>

      {activeModal === "logout" && (
        <LogoutModal onClose={() => setActiveModal(null)} t={t} />
      )}

      {activeModal === "deactivate-step1" && (
        <DeactivateModal
          onClose={() => setActiveModal(null)}
          onNext={() => setActiveModal("deactivate-step2")} // Opens Step 2
          t={t}
        />
      )}

      {activeModal === "deactivate-step2" && (
        <DeactivatePasswordModal
          onClose={() => setActiveModal(null)}
          onNext={() => setActiveModal("deactivate-step3")}
          showBanner={showBanner}
          t={t}
        />
      )}

      {activeModal === "deactivate-step3" && (
        <DeactivateOtpModal
          onClose={() => setActiveModal(null)}
          showBanner={showBanner}
          navigate={navigate}
          t={t}
        />
      )}

      {/* Delete Account Modals */}
      {activeModal === "delete-step1" && (
        <DeleteAccountModal
          onClose={closeDeleteModal}
          onNext={handleDeleteAccount}
          t={t}
        />
      )}

      {activeModal === "delete-step2" && (
        <DeletePasswordModal
          onClose={closeDeleteModal}
          onNext={handleDeleteAccount}
          password={deletePassword}
          setPassword={setDeletePassword}
          passwordError={deletePasswordError}
          setPasswordError={setDeletePasswordError}
          deletingAccount={deletingAccount}
          t={t}
        />
      )}

      {activeModal === "delete-step3" && (
        <DeleteOtpModal
          onClose={closeDeleteModal}
          onNext={handleDeleteAccount}
          otpCode={deleteOtpCode}
          setOtpCode={setDeleteOtpCode}
          otpError={deleteOtpError}
          showOtpError={deleteShowOtpError}
          verificationMethod={deleteVerificationMethod}
          userPhone={deleteUserPhone}
          hasSmsBeenSent={deleteHasSmsBeenSent}
          resendLoading={deleteResendLoading}
          emailSecondsLeft={deleteEmailSecondsLeft}
          smsSecondsLeft={deleteSmsSecondsLeft}
          onResendOtp={handleDeleteResendOtp}
          onTryAnotherWay={handleDeleteTryAnotherWay}
          onSendSms={handleDeleteSendSms}
          onOtpChange={handleDeleteOtpChange}
          onKeyPress={handleDeleteKeyPress}
          deletingAccount={deletingAccount}
          t={t}
        />
      )}

      {/* Two-Factor OTP Modal */}
      <TwoFactorOtpModal
        isOpen={showTwoFactorOtpModal}
        onClose={() => setShowTwoFactorOtpModal(false)}
        targetValue={pendingTwoFactorValue}
        onSuccess={handleTwoFactorOtpSuccess}
        userEmail={twoFactorUserEmail}
        userPhone={twoFactorUserPhone}
        userFirstName={twoFactorUserFirstName}
      />

      {/* Modal Banner */}
      {banner.show && (
        <div className={`profile-modal-banner ${banner.type}`}>
          <span className="banner-message">{banner.message}</span>
        </div>
      )}

    </div>
  );
}

export default UserProfile;
